import { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import {
  APIs,
  API_BASE_URL,
  REPORTDONESTATUS,
  SHARED_VIEWER,
  NonAuthRoutes,
} from "../../constants/Constants";
import {
  downloadPdfReport,
  getPatientReportData,
  getReportTemplatesData,
} from "../../service/StudyReportService";
import {
  activeRadiologistViewing,
  getMobileStudyData,
  getPriorStudyData,
  getSharedStudyData,
  getSharedStudyPriorsData,
  getStudyData,
  getStudyExpirationStatus,
  getStudyDataForContextSwitch,
  getSeriesByUID,
  waitForUploadProcessDone,
  isSharePasswordBypassAllowed,
  validateSharePassword
} from "../../service/StudyViewerService";
import studyviewerContext from "./StudyviewerContext";
import { getStudyCommunicationData } from "../../service/CommunicationService";
import userContext from "../users/UserContext";
import { userPreference } from "../../service/UserService";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { hideTimer, showTimer, stopTimer, updateTimer, resetTimer, clearTimer } from "../../pages/StudyViewer/Components/Unassignment/StudyExpirationTimer";
import $ from "jquery";
import useOrientationDetector from "../../components/OrientationDetector";
import { set } from "date-fns";
import { eventBus } from "../../pages/Annotations/helper/initialData";
import { getReturnReasonCategory } from "../../service/MasterService";
import { getEnv } from '../../helpers/EnvHelper';
import { setAutoExpandCineSeries } from "../../pages/StudyViewer/helpers/seriesPanelStore";
import {
  buildWadorsFrame1ImageId,
  buildSharedWadorsFrame1ImageId,
  fetchAndRegisterMultiFrameMetadata,
  registerImageMetadata,
} from "../../pages/StudyViewer/helpers/MultiFrameService";
import { loadUserDocument } from "../../service/HPV2Service";
import { selectPriorStudies } from "../../helpers/hangingProtocol/priorSelection";
import {
  ENCAPSULATED_DOC_MODALITY,
  seriesCarriesEncapsulatedPdf,
} from "../../pages/StudyViewer/helpers/EncapsulatedPDFService";
import { currentHpDevice } from "../../helpers/hangingProtocol/viewerDevice";

let initState = {
  viewport1Loading: false,
  viewport2Loading: false,
  viewport3Loading: false,
  viewport4Loading: false,
};

// Series that are documents / annotations rather than images.
const NON_IMAGE_MODALITIES = ["SR", "PR", "KO", "DOC"];

/**
 * The modality the viewer should treat a series as.
 *
 * An Encapsulated PDF is recognised by its SOP Class UID rather than its
 * modality, because PS3.3 C.24.1 lets the sender label one DOC, OT, SR or the
 * parent study's modality and all are conformant (see EncapsulatedPDFService).
 * Normalising here means the rest of the viewer — stack URL building, thumbnail
 * gating, viewport routing, the toolbar's document checks — keeps matching the
 * single "DOC" string it already matches, instead of each learning about SOP
 * Class UIDs. Without this a report PDF sent as SR reached the SR text parser,
 * which found no content sequence and rendered an almost empty report rather
 * than the document.
 *
 * Non-image series (SR/PR/KO/DOC) keep their own DICOM modality so a prior
 * study's derived series is not stamped with the study-level value. Image series
 * take the study-level (DB) modality so an edited modality is reflected — unless
 * the study modality is itself a non-image one. The study row takes the modality
 * of whichever instance arrived first, so a CT+SR+PDF study can be stored as
 * "DOC"; stamping that onto the CT series made every CT thumbnail render as
 * "PDF". Fall back to the series' own modality then.
 */
const resolveStackModality = (study, series) => {
  if (seriesCarriesEncapsulatedPdf(series)) return ENCAPSULATED_DOC_MODALITY;
  const seriesModality = series.modality?.name ?? null;
  if (seriesModality != null && NON_IMAGE_MODALITIES.includes(seriesModality)) {
    return seriesModality;
  }
  if (study.modality != null && !NON_IMAGE_MODALITIES.includes(study.modality)) {
    return study.modality;
  }
  return seriesModality;
};

const StudyviewerState = (props) => {
  const { username, userId , contextSwitchUser, contextSwitchStudyviewer } = useContext(userContext);
  const [enableHeatmap, setEnableHeatmap] = useState(false);
  const [enableOutline, setEnableOutline] = useState(false);
  const [stackSize, setStackSize] = useState(0);
  const [progress, setProgress] = useState(0);
  const [progressComplete, setProgressComplete] = useState(true);
  const [dataGathered, setDataGathered] = useState(false);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [savedKeyImages, setSavedKeyImages] = useState({});
  const [unsavedKeyImages, setUnsavedKeyImages] = useState({});
  const [keyImages, setKeyImages] = useState([]);
  const [priorStudyKeyImages, setPriorStudyKeyImages] = useState([]);
  // KO ("Mark as Key Object") images — kept separate from keyImages so they
  // only drive the viewer star/badge, not the Manage Key Images modal / save.
  const [keyObjectImages, setKeyObjectImages] = useState([]);
  const [priorStudyKeyObjectImages, setPriorStudyKeyObjectImages] = useState([]);
  const [prImages, setPrImages] = useState([]);
  const [priorStudyPRImages, setPriorStudyPRImages] = useState([]);
  const [studyPRAppliedCount, setStudyPRAppliedCount] = useState(0);
  const [studyResponseData, setStudyResponseData] = useState({});
  const [priorStudyResponseData, setPriorStudyResponseData] = useState({});
  const [studyModality, setStudyModality] = useState("");
  const [studyParentId, setParentStudyId] = useState();
  const [tempId, setTempId] = useState("");
  const [messageCount, setMessageCount] = useState(0);
  const [impressions, setImpressions] = useState({});
  const [isAccessAllowed, setIsAccessAllowed] = useState(true);
  const [notAllowedMessage, setNotAllowedMessage] = useState("");
  const [firmViewerId, setFirmViewerId] = useState(-1);
  const firmViewerIdRef = useRef(-1);
  firmViewerIdRef.current = firmViewerId;
  const [autoGenerateReportData, setAutoGenerateReportData] = useState({
    maxAIEmergency: null,
    aiProcessed: null,
  });
  const [autoOpenReportPanel, setAutoOpenReportPanel] = useState(false);
  const [allLoadingComplete, setAllLoadingComplete] = useState(false);
  const [showToast, setShowToast] = useState({
    show: false,
    message: "",
    loading: false,
    state: "success",
  });
  const [assignmentData, setAssignementData] = useState({
    studyId: null,
    clientId: null,
    firmId: null,
    providerId: null,
    assignedRadiologist: null,
  });

  const [downloadStudyEnabled, setDownloadStudyEnabled] = useState(false);

  const [studyBoundingBox, setStudyBoundingBox] = useState([]);
  const [studyTextTool, setStudyTextTool] = useState([]);
  const [studyPredictedPathology, setStudyPredictedPathology] = useState([]);
  const [viewportLoading, setViewportLoading] = useState(initState);
  const [studyHistoryData, setStudyHistoryData] = useState([]);
  const [studyClinicalNotesData, setStudyClinicalNotesData] = useState("");
  const [reportSavedBy, setReportSavedBy] = useState(0);
  const [reportMenu, setReportMenu] = useState(false);
  const [seriesMenu, setSeriesMenu] = useState(false);
  const [patientStudyData, setPatientStudyData] = useState({
    patientId: null,
    patientName: null,
    patientAge:null,
    patientSex:null,
    orderId:null,
    studyDescription:null,
    hospitalName:null,
    modality:null
  });
  const [weasisPatientDetails, setWeasisPatientDetails] = useState({
    patientId: null,
    patientName: null,
    patientAge:null,
    patientSex:null,
    orderId:null,
    modality:null, 
    studyDate : null, 
    studyIdDisplay : null
  }) 
  const [studyParentAssignmentData, setStudyParentAssignmentData] = useState(
    {}
  );
  const [isGenki, setIsGenki] = useState(false);
  const [meddreamFullUrl, setMeddreamFullUrl] = useState("");
  const [reportingDoctor, setReportingDoctor] = useState(null);
  const [parentChildReportsObj, setParentChildReportObj] = useState({});
  const [switchingToBasic, setSwitchingToBasic] = useState(false);
  const [aiProcessed, setAiProcessed] = useState(false);
  const [alreadyAddedBB, setAlreadyAddedBB] = useState(false);
  const [sharedDefaultViewer, setSharedDefaultViewer] = useState(-1);
  const [sharedAdvanceViewerURL, setSharedAdvanceViewerURL] = useState("");
  const [showStartAlertToast, setShowStartAlertToast] = useState(false);
  const [oneTimeShowAlertDone, setOneTimeShowAlertDone] = useState(false);
  const [showAttachment, setShowAttachment] = useState(false);
  const [showClinicalNotes, setShowClinicalNotes] = useState(false);
  const [forceReinferenceImageIds, setForceReinferenceImageIds] = useState([]);
  const [firstTimeLoadOnlyToast, setFirstTimeLoadOnlyToast] = useState(true);
  const [showShortcutModal, setShowShortcutModal] = useState(false);
    const [showPathologyOptions, setShowPathologyOptions] = useState(
    (isMobile || getEnv("REACT_APP_VDC_PLATFORM")=="true")? false : true
  );
  const [showAccountWarning, setShowAccountWarning] = useState(false);
  const [accountWarningText, setAccountWarningText] = useState("");
  let enableAdvanceViewerSubdomain = (getEnv("REACT_APP_ENABLE_ADVANCE_VIEWER_SUB_DOMAIN")=="false"||getEnv("REACT_APP_ENABLE_ADVANCE_VIEWER_SUB_DOMAIN")==false)?false:true;
  const [showTopBar, setShowTopBar] = useState(true);
  const isLandscape = useOrientationDetector();
  const isLandscapeRef = useRef(isLandscape);
  isLandscapeRef.current = isLandscape;
  const isChangingLayout = useRef(false);
  useEffect(() => {
    if (!isLandscape && !isChangingLayout.current) {
      isChangingLayout.current = true;
      setAutoOpenReportPanel(prev => (prev === true ? false : prev));
      setTimeout(() => { isChangingLayout.current = false; }, 500);
    }
  }, [isLandscape]);
  //   let patientResponseData = {};

  // const refreshReport = () => {
  //   getData(true);
  // }
  const [qcData, setQcData] = useState({
    clientId: null,
    firmId: null,
    radiologistId: null,
  });

  const [studyData, setStudyData] = useState({
    stacks: [],
    heatmapStacks: [],
    outlineStacks: [],
    originalStacks: [],
  });

  const [priorStudyData, setPriorStudyData] = useState([]);
  /**
   * Which priors satisfied each of the applied protocol's prior conditions, as
   * positions in priorStudyData. A viewport addresses a prior as
   * `{ conditionIndex, studyIndex }`, and this is the only thing that can resolve
   * that to a study — see helpers/hangingProtocol/priorSelection.
   */
  const [priorsByCondition, setPriorsByCondition] = useState(new Map());
  const [extReportRecieved, setExtReportRecieved] = useState(null);
  const [viewerOpacity, setViewerOpacity] = useState(1);
  const [sharedViewer, setSharedViewer] = useState(false);
  //const [enableUnassignmentService, setEnableUnassignmentService] = useState(null);
  const [reportDoneStatus, setreportDoneStatus] = useState(null);
  // Authoritative backend flag (LoadStudyResponseDto.keyImagePresent) - same
  // source of truth the study list uses. Bridged into StudyReportState so the
  // report editor download/preview menus highlight consistently in the viewer.
  const [keyImagePresent, setKeyImagePresent] = useState(false);
  const [showStudyExpirationPopup, setShowStudyExpirationPopup] = useState(false);
  const [showStudyAlreadyExpiredPopup, setShowStudyAlreadyExpiredPopup] = useState(false);
  const [checkStudyExpired, setCheckStudyExpired] = useState(false);
  const [showSharedViewer, setShowSharedViewer] = useState(getEnv("REACT_APP_VDC_PLATFORM") === "true" ? false : true);
  const [advViewerUser, setAdvViewerUser] = useState(false);
  const [isOutlineEnabled, setIsOutlineEnabled] = useState(false);
  const [isHeatMapEnabled, setIsHeatMapEnabled] = useState(false);
  const [weasisViewerUser, setWeasisViewerUser] = useState(null);
  const [infinitViewerUser, setInifinitViewerUser] = useState(false);
  const [infinitViewerUrl, setinfinitViewerUrl] = useState(null);
  const [accessionNumber, setAccessionNumber] = useState(null);
  const [keyword, setKeyword] = useState(null);
  const [prefetchCount, setPrefetchCount] = useState(-1);
  // Pauses the thumbnail preload loop. Set true when the SharedViewer switches to
  // the Advance (MedDream iframe) viewer — the thumbnail panel is hidden, so there
  // is no reason to keep downloading series in the background. ThumbnailState reads
  // this flag to break out of / resume its startPrefetching loop.
  const [prefetchPaused, setPrefetchPaused] = useState(false);
  const [twoMonitorEnabled, setTwoMonitorEnabled] = useState(false);
  const [basicWeasisUser, setBasicWeasisUser] = useState(null);
  const [isPriorsStudy, setIsPriorsStudy] = useState(false);
  //const [isEnabledSlabThickness, setIsEnabledSlabThickness] = useState(false);
  const [s10User, setS10User] = useState(false);
  const [s10Url, setS10Url] = useState(null);
  const [advanceViewerURLIndex,setAdvanceViewerURLIndex] = useState(-1);
  const [advanceViewerSlotUUID,setAdvanceViewerSlotUUID] = useState(null);

  const [stackListSortedByImages, setStackListSortedByImages] = useState([]);

  const [isTypistWorkflowEnabled, setIsTypistWorkflowEnabled] = useState(false);
  const [showChats, setShowChats] = useState(false);
  const [typistShowHistory, setTypistShowHistory] = useState(false);
  const [cxrVersion, setCXRVersion] = useState(false);
  const [boneEnabled, setBoneEnabled] = useState(false);
  const [showFindingsHighlighted, setShowFindingsHighlighted] = useState(true);
  const [showProbablilityScores, setShowProbabilityScores] = useState(true);
  const [showAboveThresholdScore, setShowAboveThresholdScore] = useState(true);
  const [nonFrontalImages, setNonFrontalImages] = useState([]);

  const [hpDataList, setHpDataList] = useState({});
  const [hpLoading, setHpLoading] = useState(false);
  const [isHPApplied, setIsHpApplied] = useState(false);
  const [appliedHp, setAppliedHp] = useState({});
  const [hpPercentageMap, setHpPercentageMap] = useState(new Map());
  // What the viewer's hanging-protocol menu lists. One entry per protocol the
  // user has, matching or not:
  //   { key, name, groupName, description, matchPercentage, applicable, hp }
  // Filled from the v2 planner when the user has a v2 document, from hpDataList
  // otherwise — so the menu has a single source whichever engine hung the study.
  const [hpCandidates, setHpCandidates] = useState([]);
  const [hpViewportMap, setHpViewportMap] = useState(new Map());
  const [hpNotFound, setHpNotFound] = useState(false);
  // Tri-state gate for the study-open series load: null while the hanging
  // protocol pipeline is still deciding, true when a protocol will hang, false
  // when none will. Thumbnail's index-0 auto-load waits on it, so a study with
  // an HP no longer paints the 1x1 default first and then jumps to the protocol
  // layout - it hangs the protocol straight away.
  const [hpWillHang, setHpWillHang] = useState(null);
  const [showSharedTopBar, setShowSharedTopBar] = useState(true);
  const [isBypassAllowed, setIsBypassAllowed] = useState(false); // true/false
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [ cINEFramesPerSecond, setCINEFramesPerSecond ] = useState(7);
  const [ returnReasonList, setReturnReasonList] = useState([]);
  const [showAiResult, setShowAiResult] = useState(false);
  const [showSavedReport, setShowSavedReport] = useState(false);

  // const dicomBaseURL = API_BASE_URL + APIs.GETIMAGEDATA;
  const dicomBaseURL = API_BASE_URL.startsWith("https")
    ? API_BASE_URL.replace("https", "dicomweb") + APIs.GETIMAGEDATAWITHFILESIZE
    : API_BASE_URL.replace("http", "dicomweb") + APIs.GETIMAGEDATAWITHFILESIZE;
  // const dicomBaseURL = API_BASE_URL.startsWith("https")
  //   ? API_BASE_URL.replace("https", "web") + APIs.GETIMAGEDATA
  //   : API_BASE_URL.replace("http", "web") + APIs.GETIMAGEDATA;
  const imageBaseURL = API_BASE_URL.startsWith("https")
    ? API_BASE_URL.replace("https", "web") + APIs.GETIMAGEDATAWITHFILESIZE
    : API_BASE_URL.replace("http", "web") + APIs.GETIMAGEDATAWITHFILESIZE;

  const sharedDicomBaseURL = API_BASE_URL.startsWith("https")
    ? API_BASE_URL.replace("https", "dicomweb") + APIs.GETSHAREDIMAGEDATA
    : API_BASE_URL.replace("http", "dicomweb") + APIs.GETSHAREDIMAGEDATA;
  const sharedImageBaseURL = API_BASE_URL.startsWith("https")
    ? API_BASE_URL.replace("https", "web") + APIs.GETSHAREDIMAGEDATA
    : API_BASE_URL.replace("http", "web") + APIs.GETSHAREDIMAGEDATA;
  const kd = false;
  const isOriginal = false;
  const { t } = useTranslation();
  const [checkStudyExpirationStatus, setCheckStudyExpirationStatus] = useState(true);

const [maskImages, setMaskImages] = useState({});

  // VAPT: advanced-viewer slots no longer live in localStorage ("advViewerSlots").
  // Each slot is now a browser Web Lock ("augmento-advViewerSlot-<n>") that the viewer tab
  // holds for as long as it is open. The browser releases it by itself when the tab closes,
  // crashes or navigates away, so a slot can no longer leak (crashed tabs, 2FA logins that
  // never reset the list), and two tabs opening at once can no longer get the same slot.
  // Slots only matter where the numbered MedDream subdomains are used
  // (REACT_APP_ENABLE_ADVANCE_VIEWER_SUB_DOMAIN, i.e. VDC); Web Locks need HTTPS or localhost.
  const ADV_VIEWER_SLOT_COUNT = 10;
  const advViewerSlotReleaseRef = useRef(null);

  const releaseAdvanceViewerSlot = useCallback(() => {
    if (advViewerSlotReleaseRef.current) {
      advViewerSlotReleaseRef.current();
      advViewerSlotReleaseRef.current = null;
      console.warn("[advViewerSlot] slot released");
    }
  }, []);

  // Resolves to the slot index (0-9) this tab now holds, or -1 when none is free or Web Locks
  // are unavailable (old browser / plain http) - the caller then uses the un-numbered URL.
  const acquireAdvanceViewerSlot = () =>
    new Promise((resolve) => {
      if (typeof navigator === "undefined" || !navigator.locks || !navigator.locks.request) {
        resolve(-1);
        return;
      }
      const tryNext = (i) => {
        if (i >= ADV_VIEWER_SLOT_COUNT) {
          resolve(-1);
          return;
        }
        navigator.locks
          .request(`augmento-advViewerSlot-${i}`, { ifAvailable: true }, (lock) => {
            if (!lock) {
              tryNext(i + 1); // taken by another tab - try the next slot
              return undefined;
            }
            resolve(i);
            // Hold the lock until releaseAdvanceViewerSlot() runs or the tab goes away.
            return new Promise((release) => {
              advViewerSlotReleaseRef.current = release;
            });
          })
          .catch((e) => {
            console.warn("[advViewerSlot] could not take a slot", e);
            resolve(-1);
          });
      };
      tryNext(0);
    });

  const getSlotAdvanceViewerUrl = async (advViewerUrl) => {
    // OLD (one-time cleanup, dropped on review): localStorage.removeItem("advViewerSlots");
    // NEW: no slot needed where numbered subdomains are off - the caller already falls back
    // to the server's URL there, exactly as before.
    if (!enableAdvanceViewerSubdomain) return null;

    releaseAdvanceViewerSlot(); // never hold two slots in one tab
    const num = await acquireAdvanceViewerSlot();
    if (num === -1) {
      // OLD (Web Locks version, first draft): console.warn("[advViewerSlot] no slot available - using the un-numbered advance viewer URL");
      console.error("No available slots in Advance Viewer."); // same message as the original localStorage code
      return null;
    }

    const advViewerSubDomain = getEnv("REACT_APP_ADVANCE_VIEWER_SUB_DOMAIN");
    const newSubdomain = `${advViewerSubDomain}${num + 1}`;
    const updatedString = advViewerUrl.replace(advViewerSubDomain, newSubdomain);
    setAdvanceViewerURLIndex(num);
    console.warn(`[advViewerSlot] slot ${num} assigned (${newSubdomain})`);
    return updatedString;

    // OLD (localStorage slot list):
    // const getSlotAdvanceViewerUrl = (advViewerUrl) => {
    // // Generate a random 5-letter UUID
    // const generateUUID = () => Math.random().toString(36).substring(2, 7).toUpperCase();
    // // Get or initialize slots with objects
    // let advViewerSlots = JSON.parse(localStorage.getItem("advViewerSlots")) || Array(10).fill({ available: 0, uuid: null });
    // const getNextAvailable = () => advViewerSlots.findIndex(slot => slot.available === 0);
    // const num = getNextAvailable();
    // if (num === -1) {
    //   console.error("No available slots in Advance Viewer.");
    //   return null;
    // }
    // const uuid = generateUUID();
    // advViewerSlots[num] = { available: 1, uuid }; // Update the slot with availability and UUID
    // localStorage.setItem("advViewerSlots", JSON.stringify(advViewerSlots));
    // const advViewerSubDomain = getEnv("REACT_APP_ADVANCE_VIEWER_SUB_DOMAIN");
    // const newSubdomain = `${advViewerSubDomain}${num + 1}`;
    // const updatedString = advViewerUrl.replace(advViewerSubDomain, newSubdomain);
    // setAdvanceViewerSlotUUID(uuid);
    // setAdvanceViewerURLIndex(num);
    // console.log(`Slot ${num} assigned with UUID: ${uuid}`);
    // return updatedString;
    // };
  };

  const fetchStudyData = useCallback(async (studyId, checkForAdv) => {
    setIsHeatMapEnabled(false);
    setIsOutlineEnabled(false);
    const advanceViewer = window.location.pathname.includes(
      "nonXrayAdvanceViewer"
    );
    let response = null;
    if(NonAuthRoutes.CONTEXTSWITCHSTUDYVIEWER === window.location.pathname && contextSwitchStudyviewer){
      response = await getStudyDataForContextSwitch();
    }else{

    
     response = await getStudyData(
      studyId,
      firmViewerIdRef.current,
      getEnv("REACT_APP_HYBRID_MODE"),
      advanceViewer
    );
  }
    if (
      response.status === 401 ||
      response.status === 400 ||
      response.status === 451
    ) {
      setIsAccessAllowed(false);
      setNotAllowedMessage(response.data.message);
      return false;
    }
    if (response.status === 200) {
      // console.log(response.data.data);
      setStudyBoundingBox(
        response.data.data?.studyAnnotationMeasurement?.imageAnnotationMeasurements
      );
      setStudyTextTool(
        response.data.data?.studyAnnotationMeasurement
          ?.imageToolAnnotationMeasurements
      );
      // setStudyBoundingBox(response.data.data.studyAnnotationMeasurement.imageAnnotationMeasurements);
      setParentStudyId(response.data.data.id);

      // console.log("Assingment data");
      // console.log(response.data.data.assignment);
      if (response.data.data.studyHistoryDocList != null) {
        setStudyHistoryData(response.data.data.studyHistoryDocList);
      }
      if (
        response.data.data.history != null ||
        response.data.data.history != undefined
      ) {
        setStudyClinicalNotesData(response.data.data.history);
      }
      setReportSavedBy(response.data.data.reportSavedBy);

      setAutoGenerateReportData(prev => ({
        ...prev,
        maxAIEmergency: response.data.data.maxAIEmergency,
        aiProcessed: response.data.data.aiProcessed,
      }));

      setAssignementData(prev => {
        const newItem = { ...prev };
        newItem.clientId =
          response.data.data.clientId == null ? 0 : response.data.data.clientId;
        newItem.firmId = response.data.data.assignment.firmId;
        newItem.providerId = response.data.data.assignment.providerId;
        newItem.studyId = response.data.data.id;
        if (
          response.data.data.assignment.radiologist != undefined ||
          response.data.data.assignment.radiologist != null
        ) {
          newItem.assignedRadiologist =
            response.data.data.assignment.radiologist.username;
        } else {
          newItem.assignedRadiologist = null;
        }
        return newItem;
      });
      if (response.data?.data?.assignment != null) {
        setStudyParentAssignmentData(response.data.data.assignment);
      }
      setPatientStudyData(prev => ({
        ...prev,
        patientId: response.data.data.patientId,
        patientName: response.data.data.patientName,
        modality: response.data.data.modality,
        studyDescription: response.data.data.studyDescription,
      }));
      setShowFindingsHighlighted(response.data.data.assignment.firm.showFindings)
      setShowProbabilityScores(response.data.data.assignment.firm.showProbability)
      setWeasisPatientDetails(prev => {
        const d = response.data.data;
        return {
          ...prev,
          patientId: d.patientId,
          patientName: d.patientName,
          patientAge: d.patientAge,
          patientSex: d.patientSex,
          orderId: d.orderId,
          modality: d.modality,
          studyDate: d.studyDateDisplay,
          studyIdDisplay: d.id + "",
          studyDescription: d.studyDescription,
          clientName: d.assignment?.cfName,
        };
      });
      setQcData(prev => ({
        ...prev,
        clientId: response.data.data.clientId,
        firmId: response.data.data.firmId,
        radiologistId: response.data.data.radiologistId,
      }));
      setIsGenki(
        response.data.data.genkyStudy && response.data.data.aiProcessed === 1
      );
      // setAdvViewerUser(response.data.data.nonXrayAdvanceViewer);
      if(getEnv("REACT_APP_VDC_PLATFORM") == "true"){
        setAdvViewerUser(response.data.data.advViewerUser);
      }else{
        setAdvViewerUser(response.data.data.nonXrayAdvanceViewer);
      }
      // setParentStudyId(response.data.data.id);
      setStudyModality(response.data.data.modality);
      setStudyPredictedPathology(response.data.data.studyPredictionList);
      // setModality(response.data.data.modality);
      setStudyResponseData(response.data.data);
      setAiProcessed(response.data.data.aiProcessed);



      setAccountWarningText(response.data.data.accountWarning || "");
      if (response.data.data.accountWarning && response.data.data.accountWarning.trim() !== "") {
        setShowAccountWarning(true);
}
      if (checkForAdv) {
        generateStudyData(response.data.data);
      } else {
        generateStudyData(response.data.data, false);
        // OLD: const url = getSlotAdvanceViewerUrl(response?.data?.data?.advanceViewerFullUrl);
        const url = await getSlotAdvanceViewerUrl(response?.data?.data?.advanceViewerFullUrl);
        console.log("URL-----------"+url);
        if (enableAdvanceViewerSubdomain && url) {
          setMeddreamFullUrl(url);
        }else{
        setMeddreamFullUrl(response?.data?.data?.advanceViewerFullUrl);
        }
      }

      setDownloadStudyEnabled(response.data.data.allowDownloadStudy);
      setDataGathered(true);
      setAutoOpenReportPanel(response.data.data.autoOpenReportPanel);
      setReportingDoctor(response.data.data.reportingDoctor);
      setExtReportRecieved(response.data.data.extReportReceived);
      setreportDoneStatus(response.data.data?.reportDoneStatus);
      setKeyImagePresent(response.data.data?.keyImagePresent === true);
      //setEnableUnassignmentService(response?.data?.data?.enableUnassignmentService);
      checkStatusForExpiration(response?.data?.data?.id, response.data.data?.reportDoneStatus, response.data.data?.reportingDoctor);
      setIsTypistWorkflowEnabled(response.data.data.isTypistWorkflowEnabled);
      setTypistShowHistory(response.data.data.showHistory);
      setShowAiResult(response.data.data.showAiResults);
      setShowSavedReport(response.data.data.showSavedReport);
      setShowChats(response.data.data.showChats);
      setCXRVersion(response.data.data.cxrVersion);
      setBoneEnabled(response.data.data.boneEnabled);
      setShowAboveThresholdScore(response.data.data.showAboveThresholdScore);
      setWeasisViewerUser(response.data.data.weasisViewerUser);
      setInifinitViewerUser(response.data.data.infinitViewerUser);
      setinfinitViewerUrl(response.data.data.infinitViewerUrl);
      setPrefetchCount(response.data.data.prefetchCount);
      setCINEFramesPerSecond(response.data.data.cineFramesPerSecond || 7);
      setBasicWeasisUser(response.data.data.basicWeasisUser);
      setAccessionNumber(response?.data?.data?.accessionNumber);
      setKeyword(response?.data?.data?.keyword);
      if(response?.data?.data?.priorsCount> 0 && response?.data?.data?.showPriors){
        setIsPriorsStudy(true);
      }

      setHpDataList(response.data.data.hpDtoList);
      if (!response.data.data.hpDtoList || response.data.data.hpDtoList.length == 0 || isMobile) {
        setHpNotFound(true);
      }
      setS10User(response?.data?.data?.s10User);
      setS10Url(response?.data?.data?.s10Url);
      //setIsEnabledSlabThickness(response?.data?.data?.enabledSlabThickness);
      //console.log("=========== enabledSlabThickness flag from API =", response?.data?.data?.enabledSlabThickness);
      return true;
    }
  }, []);

  const getPatientDetailsData =(data)  =>{
    const newItem = {...weasisPatientDetails}
    newItem.patientId = data.patientId;
    newItem.patientName = data.patientName;
    newItem.patientAge = data.patientAge;
    newItem.patientSex = data.patientSex;
    newItem.orderId = data.orderId;
    newItem.modality = data.modality;
    newItem.studyDate  = data.studyDateDisplay;
    newItem.studyIdDisplay = data.id + "";
    newItem.studyDescription = data.studyDescription;
    newItem.clientName = data.assignment?.cfName;
    return newItem;
  }

  useEffect(() => {
    if (
      checkStudyExpirationStatus &&
      reportDoneStatus != null &&
      reportingDoctor != null
    ) {
      const intervalId = setInterval(() => {
        checkStatusForExpiration(
          studyParentId,
          reportDoneStatus,
          reportingDoctor
        );
      }, 15000);
      return () => clearInterval(intervalId);
    }
  }, [
    reportDoneStatus,
    reportingDoctor,
    studyParentId,
    checkStudyExpirationStatus
  ]);

  useEffect(() => {
    checkStatusForExpiration(studyParentId, reportDoneStatus, reportingDoctor);  
  }, [checkStudyExpired]);

  const _shouldCallStudyStatusForExpiration = (reportDoneStatus, reportingDoctor) => {
   if(checkStudyExpirationStatus &&
      (reportDoneStatus == 0 || reportDoneStatus == 1) && reportingDoctor === username){
      return true;
    }
    return false;
  };

  const checkStatusForExpiration = async (studyId, reportDoneStatus, reportingDoctor) => {
    if (!_shouldCallStudyStatusForExpiration(reportDoneStatus, reportingDoctor)) {
      return;
    }

    const response = await getStudyExpirationStatus(studyId);
    if (response?.status === 200 && response?.data?.data) {
      var data = response.data.data;
      if (
        data?.unassignmentEnabled === true ||
        data?.unassignmentEnabled === "true"
      ) {
        displayTimerData(data);
        setShowStudyExpirationPopup(
          data.aboutToExpire === true || data.aboutToExpire === "true"
            ? true
            : false
        );
        setShowStudyAlreadyExpiredPopup(
          data.alreadyExpired === true || data.alreadyExpired === "true"
            ? true
            : false
        );
      } else {
        hideTimer();
        setShowStudyExpirationPopup(false);
        stopTimer();
        resetTimer();
        setCheckStudyExpirationStatus(false);
      }
    }
  };

  
  const displayTimerData = (data) =>{
    //updateTimer(data.timeLeftForExpiration, 0, setShowStudyExpirationPopup);
    const safeTime = Number(data.timeLeftForExpiration);

    updateTimer(
      isNaN(safeTime) ? 0 : safeTime,
      0,
      setShowStudyExpirationPopup
    );
    showTimer();
  }

  // Listen for firm configuration changes to reset timer if modalities are updated
  useEffect(() => {
    eventBus.on("firmConfigurationUpdated", (evtData) => {
      console.log("Firm configuration updated, checking if timer needs to be reset...");
      if (evtData?.unassignmentModalitiesChanged === true) {
        console.log("Unassignment modalities changed, resetting timer.");
        resetTimer();
        setCheckStudyExpired(prev => !prev);
      }
    });
  }, []);

  const hideSeriesAndImageCount = useCallback(() =>{
    const advanceViewer = window.location.pathname.includes("nonXrayAdvanceViewer");
    if(advanceViewer!= null && advanceViewer && weasisViewerUser != null && weasisViewerUser && infinitViewerUser != null && infinitViewerUser && weasisPatientDetails != null && weasisPatientDetails.modality != null &&
      (weasisPatientDetails.modality == "CT" || weasisPatientDetails.modality == "MR")){
        return false;
    }
    return true;
  }, [weasisViewerUser, infinitViewerUser, weasisPatientDetails]);

  /**
   * The studies to open as priors, per the applied protocol's
   * `priorStudyConditions` / `openMaxPriors`. Falls back to the full list on
   * anything unexpected: showing a prior the protocol would have hidden is a far
   * smaller failure than hiding one the radiologist needs.
   *
   * Mobile filters the same way, against the groups its device is allowed.
   */
  const selectPriorsFor = async (studyId, studyDtoList) => {
    try {
      const selection = selectPriorStudies(await loadUserDocument(), studyDtoList, {
        parentStudyId: studyId,
        device: currentHpDevice(),
      });
      if (selection.reason) {
        console.info("Priors not filtered by hanging protocol:", selection.reason);
      } else if (selection.dropped.length) {
        console.info(
          `Priors: ${selection.dropped.length} of ${studyDtoList.length - 1} hidden by "${selection.category.name}"`
        );
      }
      // Set even when nothing was filtered: an empty map is what tells the hang
      // that no viewport can address a prior, which is the safe reading.
      setPriorsByCondition(selection.byCondition ?? new Map());
      return selection.studies;
    } catch (e) {
      console.warn("Could not apply prior study conditions", e);
      setPriorsByCondition(new Map());
      return studyDtoList;
    }
  };

  const fetchPriorStudyData = useCallback(async (studyId) => {
    setIsHeatMapEnabled(false);
    setIsOutlineEnabled(false);
    const response = await getPriorStudyData(studyId, firmViewerIdRef.current);
    if (
      response.status === 401 ||
      response.status === 400 ||
      response.status === 451
    ) {
      setIsAccessAllowed(false);
      setNotAllowedMessage(response.data.message);
    }
    if (response.status === 200) {
      const accountWarning = response.data.data.accountWarning || "";
      setAccountWarningText(accountWarning);
      if (accountWarning.trim() !== "") {
        setShowAccountWarning(true);
      }

      // console.log(response.data.data.studyDtoList[0]);
      setStudyBoundingBox(
        response.data.data.studyDtoList[0].studyAnnotationMeasurement
          .imageAnnotationMeasurements
      );
      setStudyTextTool(
        response.data.data.studyDtoList[0].studyAnnotationMeasurement
          .imageToolAnnotationMeasurements
      );
      // setStudyBoundingBox(response.data.data.studyAnnotationMeasurement.imageAnnotationMeasurements);
      setParentStudyId(studyId);
      // console.log("Assingment data");
      // console.log(response.data.data.assignment);
      if (response.data.data.studyDtoList[0]?.studyHistoryDocList != null) {
        setStudyHistoryData(
          response.data.data.studyDtoList[0]?.studyHistoryDocList
        );
      }
      if (
        response.data.data.studyDtoList[0]?.history != null ||
        response.data.data.studyDtoList[0]?.history != undefined
      ) {
        setStudyClinicalNotesData(response.data.data.studyDtoList[0]?.history);
      }
      setReportSavedBy(response.data.data.studyDtoList[0].reportSavedBy);

      // Bridge the backend key-image flag for priors (parity with fetchStudyData), so the
      // report editor's Preview routes to the key-image variant instead of the plain report.
      setKeyImagePresent(
        response.data.data.studyDtoList[0]?.keyImagePresent === true
      );

      //TODO: Auto generated Report data

      setAssignementData(prev => {
        const newItem = { ...prev };
        newItem.clientId =
          response.data.data.studyDtoList[0].clientId == null
            ? 0
            : response.data.data.studyDtoList[0].clientId;
        newItem.firmId = response.data.data.studyDtoList[0].assignment?.firmId;
        newItem.providerId =
          response.data.data.studyDtoList[0].assignment?.providerId;
        newItem.studyId = response.data.data.studyDtoList[0].id;
        if (
          response.data.data.studyDtoList[0].assignment?.radiologist !=
            undefined ||
          response.data.data.studyDtoList[0].assignment?.radiologist != null
        ) {
          newItem.assignedRadiologist =
            response.data.data.studyDtoList[0].assignment?.radiologist.username;
        } else {
          newItem.assignedRadiologist = null;
        }
        return newItem;
      });
      // NEW: the priors viewer never set studyParentAssignmentData (only fetchStudyData did), so the
      // supervisor lock icon (LockStudy.jsx reads qcreviewerId/qcverifierId from it) showed green on a
      // locked study, and clicking it sent an empty body -> NPE in StudyService.supervisorAssignUnassignMe.
      if (response.data.data.studyDtoList[0].assignment != null) {
        setStudyParentAssignmentData(response.data.data.studyDtoList[0].assignment);
      } else {
        console.warn("[PRIORS_LOCK] no assignment on studyDtoList[0] - lock icon state unavailable");
      }
      setShowFindingsHighlighted(response.data.data.studyDtoList[0].assignment.firm.showFindings)
      setShowProbabilityScores(response.data.data.studyDtoList[0].assignment.firm.showProbability)
      setPatientStudyData(prev => {
        const newItem = { ...prev };
        newItem.patientId = response.data.data.studyDtoList[0].patientId;
        newItem.patientName = response.data.data.studyDtoList[0].patientName;
        return newItem;
      });
      setQcData(prev => {
        const newItem = { ...prev };
        newItem.clientId = response.data.data.studyDtoList[0].clientId;
        newItem.firmId = response.data.data.studyDtoList[0].firmId;
        newItem.radiologistId =
          response.data.data.studyDtoList[0].radiologistId;
        return newItem;
      });
      setIsGenki(
        response.data.data.genkyStudy && response.data.data.aiProcessed === 1
      );
      // setParentStudyId(response.data.data.id);
      setStudyModality(response.data.data.studyDtoList[0].modality);
      setAiProcessed(response.data.data.studyDtoList[0].aiProcessed);
      setStudyPredictedPathology(
        response.data.data.studyDtoList[0].studyPredictionList
      );
      setCXRVersion(response.data.data.studyDtoList[0].cxrVersion);
      setBoneEnabled(response.data.data.studyDtoList[0].boneEnabled);
      setShowAboveThresholdScore(response.data.data.studyDtoList[0].showAboveThresholdScore);

      // setModality(response.data.data.modality);
      // The API returns every study of the patient; the applied protocol decides
      // which of them are priors. Selecting here — before generatePriorStudyData
      // builds the stacks — is what keeps the array positional for thumbnails,
      // prefetch and viewport addressing. See helpers/hangingProtocol/priorSelection.
      const studyDtoList = await selectPriorsFor(studyId, response.data.data.studyDtoList);
      setPriorStudyResponseData({ ...response.data.data, studyDtoList });
      generatePriorStudyData(studyDtoList);
      setDownloadStudyEnabled(
        response.data.data.studyDtoList[0].allowDownloadStudy
      );
      setDataGathered(true);
      handleCreatingObjectForReports(studyDtoList);
      //TODO: setAutoOpenReportPanel
      setReportingDoctor(response.data.data.reportingDoctor);
      setAdvViewerUser(response.data.data.nonXrayAdvanceViewer);
      setPrefetchCount(response.data.data.prefetchCount);
      setCINEFramesPerSecond(response.data.data.cineFramesPerSecond || 7);
      setTwoMonitorEnabled(response.data.data.twoMonitorEnabled);
      setHpDataList(response.data.data?.studyDtoList[0]?.hpDtoList);
      if (!response.data.data?.studyDtoList[0]?.hpDtoList || response.data.data?.studyDtoList[0]?.hpDtoList?.length == 0 || isMobile) {
        setHpNotFound(true);
      }
    }
  }, []);

  const checkBypass = useCallback(async (token, isActiveData) => {
    const res = await isSharePasswordBypassAllowed(token, isActiveData);
    if (res?.status === 200) {
      setIsBypassAllowed(res.data.data);
    }
    return res;
  }, []);



  const fetchSharedStudyData = useCallback(async (shareToken, switchViewer, openViewer, isActiveData) => {
    const response = await getSharedStudyData(shareToken, isActiveData, switchViewer, openViewer);

    if (
      response.status === 401 ||
      response.status === 400 ||
      response.status === 451 ||
      response.status === 404
    ) {
      setIsAccessAllowed(false);
      setNotAllowedMessage(response.data.message);
      return false;
    }
    if (response.status === 200) {
      setParentStudyId(response.data.data.id);

      setPatientStudyData(prev => {
        const newItem = { ...prev };
        newItem.patientId = response.data.data.patientId;
        newItem.patientName = response.data.data.patientName;
        if (getEnv("REACT_APP_VDC_PLATFORM") === "true"){
          newItem.patientAge = response.data.data.patientAge;
          newItem.patientSex = response.data.data.patientSex;
          newItem.studyDescription = response.data.data.studyDescription;
          newItem.orderId = response.data.data.orderId;
          newItem.hospitalName = response.data.data.hospitalName;
          newItem.modality = response.data.data.modality;
        }
        return newItem;
      });


      if(getEnv("REACT_APP_VDC_PLATFORM") === "true"){
        setSharedAdvanceViewerURL(response.data.data.advanceViewerFullUrl);
      }

      if(getEnv("REACT_APP_VDC_PLATFORM") === "true" && (switchViewer == null || (switchViewer != null && switchViewer != "true")) && openViewer == null){
        setSharedDefaultViewer(response.data.data.defaultViewer);
      }else if(getEnv("REACT_APP_VDC_PLATFORM") === "true" && (switchViewer == null || (switchViewer != null && switchViewer != "true")) && openViewer == SHARED_VIEWER.BASIC){
        setSharedDefaultViewer(SHARED_VIEWER.BASIC);
      }else if(getEnv("REACT_APP_VDC_PLATFORM") === "true" && (switchViewer == null || (switchViewer != null && switchViewer != "true")) && (openViewer == SHARED_VIEWER.ADVANCE || openViewer != SHARED_VIEWER.BASIC)){
        setSharedDefaultViewer(SHARED_VIEWER.ADVANCE);
      }else if(getEnv("REACT_APP_VDC_PLATFORM") === "true"){
        setSharedDefaultViewer(-1);
      }

      // Series panel auto-expand follows the link creator's preference (for
      // this page only — never written over the browser's own copy).
      setAutoExpandCineSeries(response.data.data.autoExpandCineSeries === true, { persist: false });

      setStudyModality(response.data.data.modality);
      setStudyResponseData(response.data.data);
      setAiProcessed(response.data.data.aiProcessed);

      // A link that offers priors takes the same route the authenticated viewer
      // takes -- selectPriorsFor then generatePriorStudyData -- so a study hangs the
      // same way for a visitor as it does for the radiologist who shared it. The
      // protocols that drive the selection are already available on a share link:
      // loadUserDocument reads the shared (SYSTEM) set over /sharedServices.
      //
      // The single-study path stays the fallback for everything else, including a
      // priors fetch that fails. Losing the comparisons must never cost the study.
      let loadedWithPriors = false;
      if (response.data.data.sharePriors) {
        loadedWithPriors = await loadSharedPriors(
          shareToken,
          isActiveData,
          response.data.data.id
        );
      }
      if (!loadedWithPriors) {
        generateStudyData(response.data.data, true, shareToken);
      }

      setDataGathered(true);
      setAdvViewerUser(response.data.data.advViewerUser);
      setHpNotFound(true);
      return true;
    }
  }, []);

  /**
   * Loads the shared study and its priors into priorStudyData, or reports false and
   * leaves the caller to open the study on its own.
   *
   * isPriorsStudy is deliberately NOT set here, although this is the priors route.
   * That flag makes the Advance Viewer fetch prior reports over an authenticated
   * endpoint, which on a share link is a guaranteed 403 -- and share links carry no
   * report data by design.
   */
  const loadSharedPriors = useCallback(async (shareToken, isActiveData, sharedStudyId) => {
    try {
      const response = await getSharedStudyPriorsData(shareToken, isActiveData);
      const studyDtoList = response?.data?.data?.studyDtoList;
      if (response?.status !== 200 || !Array.isArray(studyDtoList) || studyDtoList.length === 0) {
        return false;
      }
      // One study back means the link offered priors but none survived resolution.
      // The single-study path renders that better than a priors layout with nothing
      // to compare against.
      if (studyDtoList.length === 1) {
        return false;
      }
      const selected = await selectPriorsFor(sharedStudyId, studyDtoList);
      setPriorStudyResponseData({ studyDtoList: selected });
      await generatePriorStudyData(selected, true, shareToken);
      return true;
    } catch (e) {
      console.warn("Could not load priors for this share link", e);
      return false;
    }
  }, []);

  const fetchMobileStudyData = useCallback(async (studyId, checkForAdv) => {
    const advanceViewer = window.location.pathname.includes(
      "nonXrayAdvanceViewer"
    );
    const response = await getMobileStudyData(
      studyId,
      firmViewerIdRef.current,
      getEnv("REACT_APP_HYBRID_MODE"),
      advanceViewer
    );
    if (
      response.status === 401 ||
      response.status === 400 ||
      response.status === 451 ||
      response.status === 404
    ) {
      setIsAccessAllowed(false);
      setNotAllowedMessage(response.data.message);
      return false;
    }
    if (response.status === 200) {
      setParentStudyId(response.data.data.id);

      if (getEnv("REACT_APP_VDC_PLATFORM") === "true") {
        if (response.data.data.defaultViewer === 0) {
          setSharedDefaultViewer(response.data.data.defaultViewer);
          setSharedAdvanceViewerURL(response.data.data.advanceViewerFullUrl);
          return;
        } else {
          setSharedDefaultViewer(response.data.data.defaultViewer);
          setSharedAdvanceViewerURL(response.data.data.advanceViewerFullUrl);
        }
      }

      setPatientStudyData(prev => {
        const newItem = { ...prev };
        newItem.patientId = response.data.data.patientId;
        newItem.patientName = response.data.data.patientName;
        return newItem;
      });

      setStudyModality(response.data.data.modality);
      setStudyResponseData(response.data.data);
      setAiProcessed(response.data.data.aiProcessed);
      setAccountWarningText(response.data.data.accountWarning || "");
      generateStudyData(response.data.data, true);
      setDataGathered(true);

      return true;
    }
  }, []);

  const getMessageCount = useCallback(async (studyParentId) => {
    let res = await getStudyCommunicationData(studyParentId);
    // console.log("--- Message count ---", res);
    if (res.status == 200 && res.data.result == "SUCCESS") {
      if (res.data.data.length > 0) {
        setMessageCount(res.data.data.length);
      }
    }
  }, []);

  useEffect(() => {
    if (
      studyParentId != null &&
      studyParentId != undefined &&
      !sharedViewer &&
      !isMobile
    )
      getMessageCount(studyParentId);
  }, [studyParentId]);

  const handleActiveTabRadiologistViewing = async (active) => {
    try {
      const res = await activeRadiologistViewing(studyParentId, active);
    } catch (e) {}
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        reportingDoctor != null &&
        studyParentId != null &&
        reportingDoctor === username
      ) {
        // console.log(
        //   "--- Viewing viewer tab ----",
        //   document.visibilityState === "visible"
        // );
        if (document.visibilityState === "visible") {
          handleActiveTabRadiologistViewing(true);
        } else {
          if (!switchingToBasic) {
            handleActiveTabRadiologistViewing(false);
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [reportingDoctor, studyParentId, switchingToBasic]);

  /**
   * Builds the stack/heatmapStack/outlineStack/originalStack (plus any
   * keyImages/keyObjectImages/prImages/nonFrontalImages entries) for ONE series. Shared by
   * generateStudyData (called once per series in its full-rebuild loop) and
   * addSingleSeriesToStudyData (called once, after a PR/KO save, to append
   * just the new series) so the two paths can never drift apart.
   */
  const buildSeriesStack = (study, series, seriesIndex, length, sharedData = false, shareToken = null) => {
    const keyImages = [];
    const keyObjectImages = [];
    const prImages = [];
    const nonFrontalImages = [];

    var originalStack = {
      imageIds: [],
    };
    var stack = {
      seriesDescription: series.seriesDescription,
      stackId: series.seriesNumber,
      seriesId: series.id,
      imageIds: [],
      keyImageIds: {},
      thumbnailImageIds: [],
      seriesIndex: seriesIndex,
      currentImageIdIndex: 0,
      modality: resolveStackModality(study, series),
      // Raw per-series DICOM modality, never overridden by the study-level value.
      // Used by the Fusion tool (PET series list) and the viewport overlay label so
      // a combined study (e.g. PET/CT) shows each series' true modality.
      seriesModality: series.modality?.name ?? null,
      length: length,
      studyId: study.id,
      studyIdDisplay: study.studyId,
      // modality: study.modality,
      studyDescription: study.studyDescription,
      studyDate: study.studyDateDisplay,
      uploadDate: study.uploadDateDisplay,
      isHeatmap: false,
      bodyPart: series.bodyPart,
      options: {
        visible: true,
        name: "ORIGINAL",
      },
      patientAge: study.patientAge,
      patientSex: study.patientSex,
      patientName: study.patientName,
      patientId: study.patientId,
      orderId: study.orderId,
      clientName: study.assignment?.cfName,
      images: [],
    };
    var heatmapStack = {
      imageIds: [],
      currentImageIdIndex: 0,
      studyId: study.id,
      options: {
        opacity: 0.5,
        visible: false,
        name: "HEATMAP",
      },
    };
    var outlineStack = {
      imageIds: [],
      studyId: study.id,
      currentImageIdIndex: 0,
      options: {
        opacity: 0.5,
        visible: false,
        name: "OUTLINE",
      },
    };
    // Populate imageIds array with the imageIds from each
    // series
    // For series with frame information, get the image url's by
    // requesting each frame
    series.imageList.forEach(function (image) {
      var imageStorageKey = "";
      var originalImageStorageKey = image.imageStorageKey;
      var heatmapMaskStorageKey = image.heatmapStorageKey;
      var outlineMaskStorageKey = image.outlineStorageKey;
      var thumbnailStorageKey = image.thumbnailStorageKey;
      var imageId = image.id;

      if (study.genkyStudy && study.aiProcessed === 1 && thumbnailStorageKey?.endsWith('.png')) {
        if (sharedData) {
          thumbnailStorageKey = sharedImageBaseURL  + thumbnailStorageKey;
          imageStorageKey = sharedImageBaseURL  + image.thumbnailStorageKey;
        } else {
          thumbnailStorageKey = imageBaseURL + imageId + "/" + thumbnailStorageKey;
          imageStorageKey = imageBaseURL + imageId + "/" + image.thumbnailStorageKey;
        }
      } else if (stack.modality === "SR" || stack.modality === "DOC") {
        // Encapsulated PDF (DOC) has no pixel data, so like SR it is fetched as
        // the raw DICOM object and parsed in the browser — see
        // EncapsulatedPDFService. Both use the storage-key endpoint rather than
        // the wadouri/dicomweb loaders, which expect an image.
        if (sharedData) {
          thumbnailStorageKey =
            API_BASE_URL + APIs.GETSHAREDIMAGEDATA  + thumbnailStorageKey;
          imageStorageKey =
            API_BASE_URL +
            APIs.GETSHAREDIMAGEDATA  +
            image.thumbnailStorageKey;
        } else {
          thumbnailStorageKey =
            API_BASE_URL + APIs.GETIMAGEDATAWITHFILESIZE + imageId + "/" + thumbnailStorageKey;
          imageStorageKey =
            API_BASE_URL + APIs.GETIMAGEDATAWITHFILESIZE + imageId + "/" + image.thumbnailStorageKey;
        }
      } else if (stack.modality === "PR") {
        if (sharedData) {
          thumbnailStorageKey =
            API_BASE_URL + APIs.GETSHAREDIMAGEDATA + thumbnailStorageKey;
          imageStorageKey =
            API_BASE_URL + APIs.GETSHAREDIMAGEDATA + image.thumbnailStorageKey;
        } else {
          thumbnailStorageKey =
            API_BASE_URL + APIs.GETIMAGEDATAWITHFILESIZE + imageId + "/" + thumbnailStorageKey;
          imageStorageKey =
            API_BASE_URL + APIs.GETIMAGEDATAWITHFILESIZE + imageId + "/" + image.thumbnailStorageKey;
        }
      } else {
        if (sharedData) {
          thumbnailStorageKey = sharedDicomBaseURL + thumbnailStorageKey;
          imageStorageKey = sharedDicomBaseURL + image.thumbnailStorageKey;
        } else {
          thumbnailStorageKey = dicomBaseURL + imageId + "/" + thumbnailStorageKey;
          imageStorageKey = dicomBaseURL + imageId + "/" + image.thumbnailStorageKey;
        }
      }
      if (originalImageStorageKey != null) {
        if (sharedData) {
          originalImageStorageKey =
            sharedDicomBaseURL + originalImageStorageKey;
        } else {
          originalImageStorageKey = dicomBaseURL + imageId + "/" + originalImageStorageKey;
        }
      }
      if (heatmapMaskStorageKey != null) {
        if (sharedData) {
          heatmapMaskStorageKey = sharedImageBaseURL + heatmapMaskStorageKey;
        } else {
          heatmapMaskStorageKey = imageBaseURL + imageId + "/" + heatmapMaskStorageKey;
        }
      }

      if (outlineMaskStorageKey != null) {
        if (sharedData) {
          outlineMaskStorageKey = sharedImageBaseURL + outlineMaskStorageKey;
        } else {
          outlineMaskStorageKey = imageBaseURL + imageId + "/" + outlineMaskStorageKey;
        }
      }

      if (image.keyImageTitle != null) {
        let keyImage = {
          imageUrl: imageStorageKey,
          keyImageTitle: image.keyImageTitle,
          state: "SAVED",
        };
        keyImages.push(keyImage);
      }

      if (image.isNonFrontal) {
        nonFrontalImages.push({
          imageId: image.id,
          imageUrl: imageStorageKey,
          isSentForForceReinference: image.isSentForForceReinference,
        });
      }
      // For un-split multi-frame instances (numberOfFrames > 1), swap the
      // stack slot from the wadouri full-file URL to the wadors per-frame
      // URL for frame 1. This stops the thumbnail viewport's setStack
      // from downloading the entire multi-frame DICOM just to render a
      // preview. The wadors loader needs DICOMweb metadata; we fire a
      // background fetch via fetchAndRegisterMultiFrameMetadata.
      // For shared studies: use the share-token-gated /sharedServices/dicomweb/
      // endpoint (permit-all, token embedded in path). Mobile without a
      // shareToken falls back to wadouri (no wadors swap).
      const hasMultiFrameUids =
        typeof image.numberOfFrames === "number"
        && image.numberOfFrames > 1
        && study.studyInstanceUId
        && series.seriesInstanceUId
        && image.sopInstanceUID;
      if (hasMultiFrameUids && (!sharedData || shareToken)) {
        const frame1ImageId = sharedData && shareToken
          ? buildSharedWadorsFrame1ImageId({
              shareToken,
              studyUid: study.studyInstanceUId,
              seriesUid: series.seriesInstanceUId,
              sopUid: image.sopInstanceUID,
            })
          : buildWadorsFrame1ImageId({
              studyId: study.id,
              studyUid: study.studyInstanceUId,
              seriesUid: series.seriesInstanceUId,
              sopUid: image.sopInstanceUID,
            });
        imageStorageKey = frame1ImageId;
        thumbnailStorageKey = frame1ImageId;
        fetchAndRegisterMultiFrameMetadata({
          studyId: study.id,
          studyUid: study.studyInstanceUId,
          seriesUid: series.seriesInstanceUId,
          sopUid: image.sopInstanceUID,
          numberOfFrames: image.numberOfFrames,
          frame1ImageId,
        });
      }

      // PR pencil marker — recorded AFTER the multi-frame swap above so its URL
      // is the stack slot the thumbnail / nav widget compare against (the
      // pre-swap wadouri URL never matched, so cine clips showed no ✏).
      if (image.isPRPresent) {
        prImages.push({ imageUrl: imageStorageKey });
      }

      // KO star: driven by key_object_description, kept out of keyImages so it
      // never feeds the Manage Key Images modal / key_image_title save flow.
      // Recorded after the multi-frame swap too, like the PR pencil, so a KO on
      // a cine clip marks the clip's stack slot (whole clip = KO, as MedDream).
      if (image.keyObjectDescription != null) {
        keyObjectImages.push({
          imageUrl: imageStorageKey,
          description: image.keyObjectDescription,
        });
      }

      stack.images.push(image);
      stack.imageIds.push(imageStorageKey);
      stack.thumbnailImageIds.push(thumbnailStorageKey);
      heatmapStack.imageIds.push(heatmapMaskStorageKey);
      outlineStack.imageIds.push(outlineMaskStorageKey);
      originalStack.imageIds.push(originalImageStorageKey);

      // Make per-instance UIDs + NumberOfFrames available to the viewer's
      // multi-frame detection path without parsing the full DICOM. NULL
      // numberOfFrames is preserved (legacy rows) so detectMultiFrame
      // falls back to its wadouri-parse path for those.
      registerImageMetadata(imageStorageKey, {
        studyId: study.id,
        studyUid: study.studyInstanceUId,
        seriesUid: series.seriesInstanceUId,
        sopUid: image.sopInstanceUID,
        numberOfFrames: image.numberOfFrames,
      });
      if (originalImageStorageKey != null) {
        registerImageMetadata(originalImageStorageKey, {
          studyId: study.id,
          studyUid: study.studyInstanceUId,
          seriesUid: series.seriesInstanceUId,
          sopUid: image.sopInstanceUID,
          numberOfFrames: image.numberOfFrames,
        });
      }
    });

    return { stack, heatmapStack, outlineStack, originalStack, keyImages, keyObjectImages, prImages, nonFrontalImages };
  };

  /**
   * Upsert exactly ONE series (a saved PR/KO, or a Secondary Capture, fetched
   * via getSeriesByUID) into studyData, WITHOUT touching any OTHER series'
   * object references — so viewports/thumbnails showing unrelated series never
   * see a reference change and never re-render/reload. This is the targeted
   * alternative to fetchStudyData()+generateStudyData() (which rebuilds every
   * series from scratch and is what caused the "viewport flash" on PR/KO/SC save).
   *
   * Mirrors generateStudyData's per-series logic exactly (via the shared
   * buildSeriesStack helper). PR/KO saves always create a brand-new series, so
   * this appends. SC (Secondary Capture) captures all land in one shared
   * "SC DICOM" series per study (the backend reuses it after the first
   * capture — see StudyScDicomUploadService), so a later capture must replace
   * that one existing stack in place instead of appending a duplicate entry.
   */
  const addSingleSeriesToStudyData = useCallback((seriesDto) => {
    // KO series aren't viewable stacks (mirrors the skip in generateStudyData) —
    // key image metadata already lives on the source series' image.keyImageTitle.
    if (!seriesDto || seriesDto?.modality?.name === "KO") return;

    const study = {
      id: studyData.studyId,
      studyId: studyData.stacks?.[0]?.studyIdDisplay,
      studyDateDisplay: studyData.studyDateDisplay,
      uploadDateDisplay: studyData.uploadDateDisplay,
      // Per-series fields below are read off `study` by buildSeriesStack for every
      // stack (studyDescription, patientName, etc.) — reuse whatever the existing
      // (unrelated) stacks already carry so the new stack matches them exactly.
      modality: studyData.stacks?.[0]?.modality,
      studyDescription: studyData.stacks?.[0]?.studyDescription,
      patientAge: studyData.stacks?.[0]?.patientAge,
      patientSex: studyData.stacks?.[0]?.patientSex,
      patientName: studyData.stacks?.[0]?.patientName,
      patientId: studyData.stacks?.[0]?.patientId,
      orderId: studyData.stacks?.[0]?.orderId,
      // buildSeriesStack reads clientName off study.assignment?.cfName, not
      // study.clientName directly (see line ~982) — mirror that shape here.
      assignment: { cfName: studyData.stacks?.[0]?.clientName },
      // genkyStudy/aiProcessed only affect the AI-thumbnail URL branch, which a
      // freshly-saved PR/KO/SC series never hits (that branch requires stack.modality
      // to be neither SR nor PR, and KO is already skipped above) — false is safe.
      genkyStudy: false,
      aiProcessed: 0,
      studyInstanceUId: undefined,
    };

    const existingIndex = studyData.stacks?.findIndex((s) => s.seriesId === seriesDto.id) ?? -1;

    if (existingIndex !== -1) {
      // Series already has a stack (e.g. the shared "SC DICOM" series gaining
      // another capture) — rebuild just that one entry so every OTHER series'
      // stack/heatmap/outline/original object keeps its exact reference and no
      // unrelated viewport re-renders.
      const built = buildSeriesStack(study, seriesDto, existingIndex, studyData.stacks.length);
      setStudyData(prev => ({
        ...prev,
        stacks: prev.stacks.map((s, i) => (i === existingIndex ? built.stack : s)),
        heatmapStacks: prev.heatmapStacks.map((s, i) => (i === existingIndex ? built.heatmapStack : s)),
        outlineStacks: prev.outlineStacks.map((s, i) => (i === existingIndex ? built.outlineStack : s)),
        originalStacks: prev.originalStacks.map((s, i) => (i === existingIndex ? built.originalStack : s)),
      }));
      setStudyResponseData(prev => ({
        ...prev,
        seriesList: (prev.seriesList ?? []).map((s) => (s.id === seriesDto.id ? seriesDto : s)),
      }));
      // built.keyImages/keyObjectImages/prImages/nonFrontalImages aren't re-merged
      // here: the only series this branch ever touches (SC DICOM captures) never
      // carries keyImageTitle/keyObjectDescription/isPRPresent/isNonFrontal, so
      // there's nothing to add — and blindly re-appending would duplicate entries
      // on every repeat capture.
      return;
    }

    const seriesIndex = studyData.stacks?.length ?? 0;
    const length = seriesIndex + 1;
    const built = buildSeriesStack(study, seriesDto, seriesIndex, length);

    setStudyData(prev => ({
      ...prev,
      stacks: [...(prev.stacks ?? []), built.stack],
      heatmapStacks: [...(prev.heatmapStacks ?? []), built.heatmapStack],
      outlineStacks: [...(prev.outlineStacks ?? []), built.outlineStack],
      originalStacks: [...(prev.originalStacks ?? []), built.originalStack],
      keyImages: [...(prev.keyImages ?? []), ...built.keyImages],
    }));
    setStackSize(prev => prev + 1);
    if (built.keyImages.length) {
      setSavedKeyImages(prevKI => [...(Array.isArray(prevKI) ? prevKI : []), ...built.keyImages]);
      setKeyImages(prevKI => [...prevKI, ...built.keyImages]);
    }
    if (built.keyObjectImages.length) {
      setKeyObjectImages(prevKO => [...(Array.isArray(prevKO) ? prevKO : []), ...built.keyObjectImages]);
    }
    if (built.prImages.length) {
      setPrImages(prevPR => [...prevPR, ...built.prImages]);
    }
    if (built.nonFrontalImages.length) {
      setNonFrontalImages(prevNF => [...prevNF, ...built.nonFrontalImages]);
    }
    // Keep studyResponseData.seriesList in sync too — useImageQuality.js and its
    // consumers (AdvTopBar, TopBar, Thumbnail, SCDICOMDetails, Annotations) read
    // quality flags off studyResponseData, not studyData.
    setStudyResponseData(prev => ({
      ...prev,
      seriesList: [...(prev.seriesList ?? []), seriesDto],
    }));
  }, [studyData]);

  /**
   * Priors (patient) viewer counterpart of addSingleSeriesToStudyData. On the
   * priors route `studyData` stays EMPTY (its setStudyData is commented out in
   * generatePriorStudyData) — every study's data lives in `priorStudyData`
   * ([0] = parent/current, [1..n] = priors). Saving SC/PR is only ever allowed
   * on the PARENT study, so a freshly-saved series must be merged into
   * priorStudyData[0]; the normal studyData merge is a no-op here, which is why
   * a saved PR previously didn't appear in the list until a full page reload.
   *
   * buildSeriesStack produces the SAME stack shape and URL patterns the priors
   * builder uses for PR (GETIMAGEDATAWITHFILESIZE) — and now the same modality
   * logic — so its output drops straight into priorStudyData[0].stacks.
   */
  const addSingleSeriesToPriorStudyData = useCallback((seriesDto) => {
    if (!seriesDto || seriesDto?.modality?.name === "KO") return;
    if (!Array.isArray(priorStudyData) || priorStudyData.length === 0) return;

    const parent = priorStudyData[0];
    const baseStack = parent.stacks?.[0];

    // Rebuild the minimal `study` shape buildSeriesStack reads, from whatever the
    // parent's existing stacks already carry so the new stack matches them exactly.
    const study = {
      id: parent.studyId,
      studyId: baseStack?.studyIdDisplay,
      studyDateDisplay: parent.studyDateDisplay,
      uploadDateDisplay: parent.uploadDateDisplay,
      modality: baseStack?.modality,
      studyDescription: baseStack?.studyDescription,
      patientAge: baseStack?.patientAge,
      patientSex: baseStack?.patientSex,
      patientName: baseStack?.patientName,
      patientId: baseStack?.patientId,
      orderId: baseStack?.orderId,
      assignment: { cfName: baseStack?.clientName },
      genkyStudy: false,
      aiProcessed: 0,
      studyInstanceUId: undefined,
    };

    const existingIndex = parent.stacks?.findIndex((s) => s.seriesId === seriesDto.id) ?? -1;
    const idx = existingIndex !== -1 ? existingIndex : (parent.stacks?.length ?? 0);
    const length = (parent.stacks?.length ?? 0) + (existingIndex !== -1 ? 0 : 1);
    // Built OUTSIDE the setState updater so buildSeriesStack's registerImageMetadata
    // side effect isn't double-invoked under StrictMode's double-render.
    const built = buildSeriesStack(study, seriesDto, idx, length);

    const mergeArr = (arr, item) =>
      existingIndex !== -1
        ? (arr ?? []).map((s, i) => (i === existingIndex ? item : s))
        : [...(arr ?? []), item];

    setPriorStudyData(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const p = prev[0];
      const updatedParent = {
        ...p,
        stacks:         mergeArr(p.stacks,         built.stack),
        heatmapStacks:  mergeArr(p.heatmapStacks,  built.heatmapStack),
        outlineStacks:  mergeArr(p.outlineStacks,  built.outlineStack),
        originalStacks: mergeArr(p.originalStacks, built.originalStack),
      };
      const next = [...prev];
      next[0] = updatedParent;
      return next;
    });

    // Keep priorStudyResponseData.studyDtoList[0].seriesList in sync too, so
    // useImageQuality (SCDICOMDetails etc.) sees the new series without a reload.
    setPriorStudyResponseData(prev => {
      if (!prev?.studyDtoList?.length) return prev;
      const dto0 = prev.studyDtoList[0];
      const exists = (dto0.seriesList ?? []).some((s) => s.id === seriesDto.id);
      const newSeriesList = exists
        ? dto0.seriesList.map((s) => (s.id === seriesDto.id ? seriesDto : s))
        : [...(dto0.seriesList ?? []), seriesDto];
      const newDtoList = [...prev.studyDtoList];
      newDtoList[0] = { ...dto0, seriesList: newSeriesList };
      return { ...prev, studyDtoList: newDtoList };
    });
  }, [priorStudyData]);

  /**
   * Dispatch a freshly-saved series to the right merge for the active viewer:
   * priorStudyData[0] on the patient (priors) route, studyData otherwise.
   */
  const mergeNewlySavedSeries = useCallback((seriesDto) => {
    if (Array.isArray(priorStudyData) && priorStudyData.length > 0) {
      addSingleSeriesToPriorStudyData(seriesDto);
    } else {
      addSingleSeriesToStudyData(seriesDto);
    }
  }, [priorStudyData, addSingleSeriesToPriorStudyData, addSingleSeriesToStudyData]);

  /**
   * Post-save flow for SaveAsPRButton / MarkAsKOButton: waits for the async
   * DICOM ingestion behind `processId` to actually finish (polling the same
   * upload-process-status endpoint the regular DICOM upload progress bar uses),
   * then fetches ONLY the new series (by the UID the caller generated and sent
   * in the save-pr/save-ko payload) and merges it in via addSingleSeriesToStudyData.
   *
   * Replaces the old flat 5s-wait-then-refetch-everything-via-fetchStudyData
   * approach for these two callers — that full re-fetch/rebuild is what caused
   * the viewport flash.
   *
   * @returns {Promise<{success: boolean, status?: string, message?: string}>}
   */
  const waitForSeriesReadyAndMerge = useCallback(async (processId, newSeriesInstanceUID) => {
    const statusResult = await waitForUploadProcessDone(processId);
    if (!statusResult.success) {
      return statusResult;
    }

    const response = await getSeriesByUID(studyParentId, firmViewerIdRef.current, newSeriesInstanceUID);
    const seriesDto = response?.data?.data;
    if (response?.status !== 200 || !seriesDto) {
      return {
        success: false,
        status: "SERIES_FETCH_FAILED",
        message: response?.data?.message,
      };
    }

    mergeNewlySavedSeries(seriesDto);
    return { success: true };
  }, [studyParentId, mergeNewlySavedSeries]);

  /**
   * Post-save flow for the Secondary-Capture ("Copy to clipboard") upload in
   * Viewport.jsx. Unlike PR/KO, the SC upload endpoint doesn't hand back a
   * processId to poll — uploadScDicom's HTTP response returns as soon as the
   * file is handed to the DICOM acceptor, before the acceptor's own async
   * ingestion pipeline has created the Images/Series DB rows, let alone
   * flagged them queryable. A single flat-delay-then-fetch attempt raced that
   * pipeline and normally lost, so getSeriesByUID came back empty and the
   * capture silently never appeared (only a full page reload — which re-runs
   * fetchStudyData well after ingestion is long done — showed it).
   *
   * So this polls getSeriesByUID like waitForUploadProcessDone polls the
   * upload-status endpoint for PR/KO: keep re-fetching until the series comes
   * back with at least one image, then merge it in via addSingleSeriesToStudyData
   * (upsert — repeat captures replace the existing "SC DICOM" series stack in
   * place) instead of refetching and rebuilding the entire study, which is what
   * caused the viewport flash on every capture.
   *
   * @returns {Promise<{success: boolean, status?: string, message?: string}>}
   */
  const waitForScSeriesAndMerge = useCallback(async (
    seriesInstanceUID,
    { initialDelayMs = 2000, intervalMs = 2000, timeoutMs = 30000, sopInstanceUid = null } = {}
  ) => {
    await new Promise((resolve) => setTimeout(resolve, initialDelayMs));

    // Every capture after the first lands in the already-populated "SC DICOM"
    // series, so "has any image" is true before the new one is ingested. When
    // the caller knows the capture's SOP UID, wait for that image instead.
    const isReady = (seriesDto) => sopInstanceUid
      ? seriesDto?.imageList?.some((image) => image.sopInstanceUID === sopInstanceUid)
      : seriesDto?.imageList?.length;

    const startedAt = Date.now();
    let lastResponse;
    for (;;) {
      lastResponse = await getSeriesByUID(studyParentId, firmViewerIdRef.current, seriesInstanceUID);
      const seriesDto = lastResponse?.data?.data;
      if (lastResponse?.status === 200 && isReady(seriesDto)) {
        mergeNewlySavedSeries(seriesDto);
        return { success: true };
      }
      if (Date.now() - startedAt >= timeoutMs) {
        return {
          success: false,
          status: "SERIES_FETCH_TIMEOUT",
          message: lastResponse?.data?.message,
        };
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }, [studyParentId, mergeNewlySavedSeries]);

  const generateStudyData = useCallback(async (responseData, sharedData = false, shareToken = null) => {
    let stacks = [];
    let heatmaps = [];
    let outlines = [];
    let originals = [];
    var keyImages = [];
    var keyObjectImages = [];
    var prImages = [];
    var nonFrontalImages = [];
    // responseData.studyDtoList.forEach((study) => {
    var study = responseData;
    var seriesIndex = 0;
    var length = study.seriesList.length;
    study.studyPredictionList.forEach((pred) => {
      if (
        pred.heatmapStorageKey != null &&
        pred.heatmapStorageKey.length != 0 &&
        pred.heatmap == true
      ) {
        length = length + 1;
      }
    });

    study.seriesList.forEach(function (series) {
      // Skip KO (Key Object Selection) series — they are not viewable images.
      // Key image metadata is already captured via image.keyImageTitle on regular series.
      if (series.modality?.name === "KO") return;

      const built = buildSeriesStack(study, series, seriesIndex++, length, sharedData, shareToken);
      stacks.push(built.stack);
      heatmaps.push(built.heatmapStack);
      outlines.push(built.outlineStack);
      originals.push(built.originalStack);
      keyImages.push(...built.keyImages);
      keyObjectImages.push(...built.keyObjectImages);
      prImages.push(...built.prImages);
      nonFrontalImages.push(...built.nonFrontalImages);
    });
    // });

    setStudyData(prev => ({
      ...prev,
      studyId: study.id,
      studyDateDisplay: study.studyDateDisplay,
      uploadDateDisplay: study.uploadDateDisplay,
      stacks: stacks,
      heatmapStacks: heatmaps,
      outlineStacks: outlines,
      originalStacks: originals,
      keyImages: keyImages,
    }));
    setStackSize(stacks.length);

    setSavedKeyImages(keyImages);
    setKeyImages(keyImages);
    setKeyObjectImages(keyObjectImages);
    setPrImages(prImages);
    setNonFrontalImages(nonFrontalImages);

    // console.log(savedKeyImages);
  }, []);

  useEffect(() => {
    if (!studyData.stacks?.length) return;
    const sorted = studyData.stacks
      .map((stack, index) => ({ index, length: stack.thumbnailImageIds.length }))
      .sort((a, b) => a.length - b.length);
    setStackListSortedByImages(prev =>
      JSON.stringify(prev) === JSON.stringify(sorted) ? prev : sorted
    );
  }, [studyData.stacks]);

  
  useEffect(() => {
    if(priorStudyData && priorStudyData.length>0){
     const sortedIndexLengthMap = priorStudyData[0].stacks
        .map((stack, index) => ({ index, length: stack.thumbnailImageIds.length }))  // Map index and imageIds length
        .sort((a, b) => a.length - b.length);  
      
      setStackListSortedByImages(sortedIndexLengthMap);
    }
  },[priorStudyData]);

  // useEffect(() =>{
  //   console.log(" -------- STACK DATA IN ORDER --------- ");
  //   console.log(stackListSortedByImages);
  // },[stackListSortedByImages]);

  const removeTags = (str) => {
    if (str === null || str === "") return false;
    else str = str.toString();
    return str.replace(/(<([^>]+)>|&nbsp;|\r)/gi, "");
    // return str.replace(/<.*?>/, "");
  };

  useEffect(() => {
    if (
      (studyHistoryData?.length > 0 ||
        studyClinicalNotesData.length > 0 ||
        messageCount > 0) &&
        firstTimeLoadOnlyToast &&
      (allLoadingComplete ||
        window.location.href.includes("nonXrayAdvanceViewer")) &&
      !isMobile
    ) {
      setShowStartAlertToast(true);
      setFirstTimeLoadOnlyToast(false);
    }
  }, [
    studyHistoryData,
    studyClinicalNotesData,
    messageCount,
    allLoadingComplete,
  ]);

  const handleCreatingObjectForReports = async (responseData) => {
    let allReportsObj = {};
    responseData.map((study, index) => {
      if (allReportsObj.id == undefined) {
        allReportsObj[study.id] = study;
      }
    });
    setParentChildReportObj(allReportsObj);
  };

  /**
   * Builds the impressions map the viewers overlay on a viewport, keyed "study_<id>".
   *
   * Shared by both viewers. The basic viewer gets it as a by-product of
   * generatePriorStudyData; the Advance Viewer has no use for the rest of that function
   * (MedDream renders the images) but still needs the impressions, so
   * fetchPriorStudyReportsOnly builds them on its own -- see helpers/MedDreamIntegration,
   * which pushes them into the viewer's viewport corner labels.
   *
   * hasImpression separates a real impression from the "not available" / "not reported"
   * placeholders. The basic viewer shows those placeholders as-is; the Advance Viewer
   * skips them rather than spending a corner label on "not reported yet" in every viewport
   * of the study currently being reported.
   */
  const buildImpressions = useCallback(
    (studyDtoList) => {
      const built = {};
      (studyDtoList || []).forEach((study, studyIndex) => {
        const impression = [];
        let impressionText = "";

        (study.studyReportList || []).forEach((studyReport) => {
          if (!studyReport.finalReport) return;
          const impressionWithTagsIndex =
            studyReport.finalReport.search("IMPRESSION");
          if (impressionWithTagsIndex < 0) return;
          const impressionWithTags = studyReport.finalReport.slice(
            impressionWithTagsIndex,
            studyReport.finalReport.length
          );
          const extractedImpressionText =
            extractTextFromHTML(impressionWithTags);
          if (
            extractedImpressionText !== null &&
            extractedImpressionText.length > 0
          ) {
            impressionText +=
              impressionText.length > 0
                ? "<br/>" + extractedImpressionText
                : extractedImpressionText;
            impression.push(impressionText);
          }
        });

        const hasImpression = impression.length > 0;
        if (!hasImpression) {
          impressionText +=
            study.reportDoneStatus === REPORTDONESTATUS.REPORTED
              ? t("string-literals.impression-not-available")
              : t("string-literals.impression-unreported");
        }

        built["study_" + study.id] = {
          studyDate: study.studyDateDisplay,
          uploadDate: study.uploadDateDisplay,
          impressionText,
          hasImpression,
          studyId: study.id,
          // Identity, so a reader can tell which study an impression belongs to. The
          // Advance Viewer panel leans on these: it has no thumbnail list of its own to
          // cross-reference, because MedDream owns that panel.
          modality: study.modality,
          studyDescription: study.studyDescription,
          bodyPart: (study.seriesList || []).find(
            (series) => !["KO", "SR", "PR"].includes(series.modality?.name)
          )?.bodyPart,
          // studyDtoList is ordered with the opened study first; the rest are priors.
          isCurrent: studyIndex === 0,
        };
      });
      return built;
    },
    [t]
  );

  /**
   * The reports half of fetchPriorStudyData, for the Advance Viewer.
   *
   * The Advance Viewer never calls fetchPriorStudyData -- MedDream renders the images, so
   * there is nothing to build Cornerstone stacks, thumbnails or prefetch queues for, and
   * generatePriorStudyData is a large amount of work the embedded viewer cannot use. But
   * the report panel still needs each prior's studyReportList to be able to show a prior's
   * report, which is what parentChildReportsObj carries. So this hits the same endpoint and
   * keeps only the report-facing state.
   *
   * priorStudyResponseData is set alongside because AnnotationState reads studyDtoList from
   * it to decide a prior's fromActiveDatabase flag when fetching that prior's pathologies.
   */
  const fetchPriorStudyReportsOnly = useCallback(async (studyId) => {
    const response = await getPriorStudyData(studyId, firmViewerIdRef.current);
    if (response?.status !== 200) return null;

    const studyDtoList = response.data.data.studyDtoList;
    if (!studyDtoList || studyDtoList.length === 0) return null;

    setPriorStudyResponseData(response.data.data);
    handleCreatingObjectForReports(studyDtoList);
    // What the Advance Viewer overlays on MedDream's viewport corner labels.
    setImpressions(buildImpressions(studyDtoList));
    return studyDtoList;
  }, [buildImpressions]);

  // Impressions are pulled out of the stored report HTML. Report bodies are not
  // written by one generator: editor-authored reports wrap each impression line in
  // <div>, while auto/AI-generated reports (no report_template_id) use <p>. The old
  // parser sliced from a literal "<div>" and therefore silently dropped the
  // impression for every <p>-based report. Parse the markup instead of scanning it.
  const extractTextFromHTML = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Recursive function to extract text lines
    const getTextLines = (node) => {
      let lines = [];
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent.trim();
          if (text) lines.push(text);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const childLines = getTextLines(child);
          if (childLines.length > 0) lines.push(...childLines); // flatten array
        }
      });
      return lines;
    };

    let allLines = getTextLines(doc.body);

    // Remove "IMPRESSION:" at the start if present
    if (allLines.length > 0 && /^IMPRESSION:/i.test(allLines[0])) {
      allLines[0] = allLines[0].replace(/^IMPRESSION:\s*/i, "").trim();
      if (!allLines[0]) allLines.shift(); // remove if empty
    }

    // Join lines with <br /> to preserve line breaks
    return allLines.join("<br />");
  };

  /**
   * Builds the stacks for the priors route.
   *
   * `sharedData` / `shareToken` put it on the share link's endpoints instead of the
   * authenticated ones, exactly as buildSeriesStack does for the single study -- a
   * share-link visitor has no JWT, so every url built here would 403 without it.
   *
   * Impressions are deliberately not built in shared mode. A share link shows images
   * and nothing written about them, which is what it did before priors existed; see
   * the note below.
   */
  const generatePriorStudyData = useCallback(async (responseData, sharedData = false, shareToken = null) => {
    let stackSize = 0;
    let priorStudies = [];
    let stacks = [];
    let heatmaps = [];
    let outlines = [];
    let originals = [];
    var keyImages = [];
    var keyObjectImages = [];
    var prImages = [];
    var nonFrontalImages = [];
    // responseData.studyDtoList.forEach((study) => {
    var seriesIndex = 0;
    responseData.map((study, index) => {
      var length = study.seriesList.length;
      study.studyPredictionList.forEach((pred) => {
        if (
          pred.heatmapStorageKey != null &&
          pred.heatmapStorageKey.length != 0 &&
          pred.heatmap == true
        ) {
          length = length + 1;
        }
      });
      study.seriesList.forEach(function (series) {
        // Skip KO (Key Object Selection) series — not viewable images
        if (series.modality?.name === "KO") return;

        var originalStack = {
          studyId: study.id,
          imageIds: [],
        };
        var stack = {
          seriesDescription: series.seriesDescription,
          stackId: series.seriesNumber,
          seriesId: series.id,
          imageIds: [],
          keyImageIds: {},
          thumbnailImageIds: [],
          seriesIndex: seriesIndex++,
          currentImageIdIndex: 0,
          // Same rule as the single-study builder, so the two cannot drift: a
          // prior study's PR/SR/KO series keeps its own modality, and an
          // Encapsulated PDF is recognised by SOP Class UID. See
          // resolveStackModality.
          modality: resolveStackModality(study, series),
          // Raw per-series DICOM modality, never overridden by the study-level value — see generateStudyData.
          seriesModality: series.modality?.name ?? null,
          length: length,
          studyId: study.id,
          studyIdDisplay: study.studyId,
          // modality: study.modality,
          studyDescription: study.studyDescription,
          studyDate: study.studyDateDisplay,
          uploadDate: study.uploadDateDisplay,
          isHeatmap: false,
          bodyPart: series.bodyPart,
          options: {
            visible: true,
            name: "ORIGINAL",
          },
          patientAge: study.patientAge,
          patientSex: study.patientSex,
          patientName: study.patientName,
          patientId: study.patientId,
          images: [],
        };
        var heatmapStack = {
          imageIds: [],
          currentImageIdIndex: 0,
          studyId: study.id,
          options: {
            opacity: 0.5,
            visible: false,
            name: "HEATMAP",
          },
          modality: stack.modality,
        };
        var outlineStack = {
          imageIds: [],
          studyId: study.id,
          currentImageIdIndex: 0,
          options: {
            opacity: 0.5,
            visible: false,
            name: "OUTLINE",
          },
          modality: stack.modality,
        };
        // Populate imageIds array with the imageIds from each
        // series
        // For series with frame information, get the image url's by
        // requesting each frame
        series.imageList.forEach(function (image) {
          var imageStorageKey = "";
          var originalImageStorageKey = image.imageStorageKey;
          var heatmapMaskStorageKey = image.heatmapStorageKey;
          var outlineMaskStorageKey = image.outlineStorageKey;
          var thumbnailStorageKey = image.thumbnailStorageKey;
          var imageId = image.id;
          // console.log(study.genkyStudy)
          // console.log(study.aiProcessed === 1)
          if (study.genkyStudy && study.aiProcessed === 1 && thumbnailStorageKey?.endsWith('.png')) {
            if (sharedData) {
              thumbnailStorageKey = sharedImageBaseURL + thumbnailStorageKey;
              imageStorageKey = sharedImageBaseURL + image.thumbnailStorageKey;
            } else {
              thumbnailStorageKey = imageBaseURL + imageId + "/"  + thumbnailStorageKey;
              imageStorageKey = imageBaseURL + imageId + "/"  + image.thumbnailStorageKey;
            }
          } else if (stack.modality === "SR") {
            if (sharedData) {
              thumbnailStorageKey =
                API_BASE_URL + APIs.GETSHAREDIMAGEDATA + thumbnailStorageKey;
              imageStorageKey =
                API_BASE_URL + APIs.GETSHAREDIMAGEDATA + image.thumbnailStorageKey;
            } else {
              thumbnailStorageKey =
                API_BASE_URL + APIs.GETIMAGEDATA + thumbnailStorageKey;
              imageStorageKey =
                API_BASE_URL + APIs.GETIMAGEDATA + image.thumbnailStorageKey;
            }
          } else if (stack.modality === "DOC") {
            // Encapsulated PDF is fetched as the raw DICOM object and parsed in
            // the browser (EncapsulatedPDFService), so it needs the storage-key
            // endpoint rather than the wadouri/dicomweb loaders. It uses the
            // id-qualified form — the same one the single-study builder and the
            // PR branch below use — not the SR branch's id-less variant.
            if (sharedData) {
              thumbnailStorageKey =
                API_BASE_URL + APIs.GETSHAREDIMAGEDATA + thumbnailStorageKey;
              imageStorageKey =
                API_BASE_URL + APIs.GETSHAREDIMAGEDATA + image.thumbnailStorageKey;
            } else {
              thumbnailStorageKey =
                API_BASE_URL + APIs.GETIMAGEDATAWITHFILESIZE + imageId + "/" + thumbnailStorageKey;
              imageStorageKey =
                API_BASE_URL + APIs.GETIMAGEDATAWITHFILESIZE + imageId + "/" + image.thumbnailStorageKey;
            }
          } else if (stack.modality === "PR") {
            // PR series must be fetched via GETIMAGEDATAWITHFILESIZE — the SAME
            // endpoint the single-study builder uses (see generateStudyData). The
            // priors builder previously fell through to dicomBaseURL, which returns
            // a different representation whose parsed PR was missing fields such as
            // the Content Creator's Name (0070,0084) — so the creator name showed in
            // the single-study viewer but was blank in the patient (priors) viewer.
            if (sharedData) {
              thumbnailStorageKey =
                API_BASE_URL + APIs.GETSHAREDIMAGEDATA + thumbnailStorageKey;
              imageStorageKey =
                API_BASE_URL + APIs.GETSHAREDIMAGEDATA + image.thumbnailStorageKey;
            } else {
              thumbnailStorageKey =
                API_BASE_URL + APIs.GETIMAGEDATAWITHFILESIZE + imageId + "/" + thumbnailStorageKey;
              imageStorageKey =
                API_BASE_URL + APIs.GETIMAGEDATAWITHFILESIZE + imageId + "/" + image.thumbnailStorageKey;
            }
          } else {
            if (sharedData) {
              thumbnailStorageKey = sharedDicomBaseURL + thumbnailStorageKey;
              imageStorageKey = sharedDicomBaseURL + image.thumbnailStorageKey;
            } else {
              thumbnailStorageKey = dicomBaseURL + imageId + "/"  + thumbnailStorageKey;
              imageStorageKey = dicomBaseURL + imageId + "/"  + image.thumbnailStorageKey;
            }
          }
          if (originalImageStorageKey != null) {
            originalImageStorageKey = sharedData
              ? sharedDicomBaseURL + originalImageStorageKey
              : dicomBaseURL + imageId + "/"  + originalImageStorageKey;
          }
          if (heatmapMaskStorageKey != null)
            heatmapMaskStorageKey = sharedData
              ? sharedImageBaseURL + heatmapMaskStorageKey
              : imageBaseURL + imageId + "/"  + heatmapMaskStorageKey;

          if (outlineMaskStorageKey != null)
            outlineMaskStorageKey = sharedData
              ? sharedImageBaseURL + outlineMaskStorageKey
              : imageBaseURL + imageId + "/"  + outlineMaskStorageKey;

          if (image.keyImageTitle != null) {
            let keyImage = {
              imageUrl: imageStorageKey,
              keyImageTitle: image.keyImageTitle,
              state: "SAVED",
            };
            keyImages.push(keyImage);
          }

          if (image.isNonFrontal) {
            nonFrontalImages.push({
              imageId: image.id,
              imageUrl: imageStorageKey,
              isSentForForceReinference: image.isSentForForceReinference,
            });
          }

          // Multi-frame priors use the SAME wadors per-frame swap as the primary
          // study (generateStudyData): swap the stack slot from the wadouri
          // full-file URL to the wadors frame-1 URL so the thumbnail/viewport
          // fetch ONE frame instead of downloading the whole cine file, and
          // register the DICOMweb metadata in the background.
          //
          // On a share link the swap goes to the share-token dicomweb route, whose
          // instance lookup accepts the shared study and the priors that link offers
          // -- see SharedStudyPriorService. Without a token there is no unauthenticated
          // route to swap to, so it falls back to wadouri, matching buildSeriesStack.
          const hasMultiFrameUids =
            typeof image.numberOfFrames === "number"
            && image.numberOfFrames > 1
            && study.studyInstanceUId
            && series.seriesInstanceUId
            && image.sopInstanceUID;
          if (hasMultiFrameUids && (!sharedData || shareToken)) {
            const frame1ImageId = sharedData && shareToken
              ? buildSharedWadorsFrame1ImageId({
                  shareToken,
                  studyUid: study.studyInstanceUId,
                  seriesUid: series.seriesInstanceUId,
                  sopUid: image.sopInstanceUID,
                })
              : buildWadorsFrame1ImageId({
                  studyId: study.id,
                  studyUid: study.studyInstanceUId,
                  seriesUid: series.seriesInstanceUId,
                  sopUid: image.sopInstanceUID,
                });
            imageStorageKey = frame1ImageId;
            thumbnailStorageKey = frame1ImageId;
            fetchAndRegisterMultiFrameMetadata({
              studyId: study.id,
              studyUid: study.studyInstanceUId,
              seriesUid: series.seriesInstanceUId,
              sopUid: image.sopInstanceUID,
              numberOfFrames: image.numberOfFrames,
              frame1ImageId,
            });
          }

          // PR pencil marker — after the multi-frame swap (see generateStudyData).
          if (image.isPRPresent) {
            prImages.push({ imageUrl: imageStorageKey });
          }

          // KO star — also after the multi-frame swap (see buildSeriesStack).
          if (image.keyObjectDescription != null) {
            keyObjectImages.push({
              imageUrl: imageStorageKey,
              description: image.keyObjectDescription,
            });
          }

          stack.images.push(image);
          stack.imageIds.push(imageStorageKey);
          stack.thumbnailImageIds.push(thumbnailStorageKey);
          heatmapStack.imageIds.push(heatmapMaskStorageKey);
          outlineStack.imageIds.push(outlineMaskStorageKey);
          originalStack.imageIds.push(originalImageStorageKey);

          registerImageMetadata(imageStorageKey, {
            studyId: study.id,
            studyUid: study.studyInstanceUId,
            seriesUid: series.seriesInstanceUId,
            sopUid: image.sopInstanceUID,
            numberOfFrames: image.numberOfFrames,
          });
          if (originalImageStorageKey != null) {
            registerImageMetadata(originalImageStorageKey, {
              studyId: study.id,
              studyUid: study.studyInstanceUId,
              seriesUid: series.seriesInstanceUId,
              sopUid: image.sopInstanceUID,
              numberOfFrames: image.numberOfFrames,
            });
          }
        });
        // Add the series stack to the stacks array

        stacks.push(stack);
        heatmaps.push(heatmapStack);
        outlines.push(outlineStack);
        originals.push(originalStack);
      });
      priorStudies.push({
        studyId: study.id,
        studyDateDisplay: study.studyDateDisplay,
        uploadDateDisplay: study.uploadDateDisplay,
        stacks,
        heatmapStacks: heatmaps,
        outlineStacks: outlines,
        originalStacks: originals,
        keyImages,
      });
      if (index === 0) {
        setSavedKeyImages(keyImages);
        setKeyImages(keyImages);
        setKeyObjectImages(keyObjectImages);
        setPrImages(prImages);
        setNonFrontalImages(nonFrontalImages);
        keyImages = [];
        keyObjectImages = [];
        prImages = [];
        nonFrontalImages = [];
      } else {
        setPriorStudyKeyImages(keyImages);
        setPriorStudyKeyObjectImages(keyObjectImages);
        setPriorStudyPRImages(prImages);
      }
      stackSize++;
      stacks = [];
      heatmaps = [];
      outlines = [];
      originals = [];
    });

    // setStudyData({
    //   ...studyData,
    //   stacks: stacks,
    //   heatmapStacks: heatmaps,
    //   outlineStacks: outlines,
    //   originalStacks: originals,
    //   keyImages: keyImages,
    // });
    setStackSize(stackSize);
    setPriorStudyData(priorStudies);
    // Share links carry images, never anything written about them. The shared study
    // route has never returned studyReportList, so this would build an empty map
    // anyway -- it is skipped explicitly so that a later change to what that route
    // returns cannot quietly start publishing priors' impressions to a link whose
    // recipient was never authenticated.
    if (!sharedData) {
      setImpressions(buildImpressions(responseData));
    }
    // setStackSize(stacks.length);

    console.log(priorStudies);
  }, [t]);

  const checkForMaskingAnnotation = useCallback((annotationId) => {
    for (const [imageId, imageData] of Object.entries(maskImages)) {
      const annotations = imageData.maskAnnotationData || [];
      if (annotations.some(a => a.annotationUID === annotationId)) {
        return {imageId, ...imageData};
      }
    }
    return null;
  }, [maskImages]);

  useEffect(()=>{
    if(showStudyExpirationPopup || showStudyAlreadyExpiredPopup){
      eventBus.dispatch("toggleFullscreen", {
                      show: true,
                  });
    }
  }, [showStudyAlreadyExpiredPopup, showStudyExpirationPopup]);

  const fetchReturnReasonList = async () => {
    try {
        const response = await getReturnReasonCategory();
        setReturnReasonList(response.data.data || []);
        
    } catch (err) {
        console.error(err);
        
    } 
  }

  useEffect(() => {
    if(studyParentId != null && !sharedViewer)
     fetchReturnReasonList();  
  }, [studyParentId, sharedViewer]);

  const contextValue = useMemo(() => ({
        fetchStudyData,
        addSingleSeriesToStudyData,
        waitForSeriesReadyAndMerge,
        waitForScSeriesAndMerge,
        studyData,
        setStudyData,
        studyResponseData,
        studyModality,
        studyParentId,
        generateStudyData,
        showToast,
        assignmentData,
        setShowToast,
        studyBoundingBox,
        studyTextTool,
        setStudyBoundingBox,
        studyPredictedPathology,
        viewportLoading,
        setViewportLoading,
        savedKeyImages,
        setSavedKeyImages,
        setUnsavedKeyImages,
        unsavedKeyImages,
        keyImages,
        setKeyImages,
        keyObjectImages,
        setKeyObjectImages,
        priorStudyKeyObjectImages,
        setPriorStudyKeyObjectImages,
        qcData,
        studyHistoryData,
        studyClinicalNotesData,
        messageCount,
        setMessageCount,
        progress,
        setProgress,
        progressComplete,
        setProgressComplete,
        stackSize,
        setStackSize,
        dataGathered,
        setDataGathered,
        thumbnailLoaded,
        setThumbnailLoaded,
        generatePriorStudyData,
        fetchPriorStudyData,
        priorStudyResponseData,
        setPriorStudyResponseData,
        priorStudyData,
        priorsByCondition,
        patientStudyData,
        impressions,
        downloadStudyEnabled,
        isAccessAllowed,
        setIsAccessAllowed,
        notAllowedMessage,
        setNotAllowedMessage,
        enableHeatmap,
        setEnableHeatmap,
        enableOutline,
        setEnableOutline,
        firmViewerId,
        reportDoneStatus,
        keyImagePresent,
        setKeyImagePresent,
        reportingDoctor,
        setFirmViewerId,
        reportSavedBy,
        setReportSavedBy,
        reportMenu, setReportMenu,
        seriesMenu, setSeriesMenu,
        isGenki,
        autoGenerateReportData,
        setAutoGenerateReportData,
        autoOpenReportPanel,
        meddreamFullUrl,
        setMeddreamFullUrl,
        advanceViewerURLIndex,
        advanceViewerSlotUUID,
        releaseAdvanceViewerSlot,
        priorStudyKeyImages,
        setPriorStudyKeyImages,
        prImages,
        setPrImages,
        priorStudyPRImages,
        setPriorStudyPRImages,
        studyPRAppliedCount,
        setStudyPRAppliedCount,
        parentChildReportsObj,
        fetchPriorStudyReportsOnly,
        setSwitchingToBasic,
        extReportRecieved,
        getMessageCount,
        studyParentAssignmentData,
        aiProcessed,
        viewerOpacity,
        setViewerOpacity,
        alreadyAddedBB,
        setAlreadyAddedBB,
        fetchSharedStudyData,
        checkBypass,
        isBypassAllowed,
        isPasswordValid,
        sharedViewer,
        setSharedViewer,
        sharedDefaultViewer,
        setSharedDefaultViewer,
        sharedAdvanceViewerURL,
        setSharedAdvanceViewerURL,
        fetchMobileStudyData,
        showStartAlertToast,
        setShowStartAlertToast,
        setOneTimeShowAlertDone,
        showAttachment,
        setShowAttachment,
        showClinicalNotes,
        setShowClinicalNotes,
        setAllLoadingComplete,
        showStudyExpirationPopup,
        setShowStudyExpirationPopup,
        showStudyAlreadyExpiredPopup,
        setShowStudyAlreadyExpiredPopup,
        checkStudyExpired, setCheckStudyExpired,
        setIsTypistWorkflowEnabled,isTypistWorkflowEnabled,
        setShowChats,showChats,
        setTypistShowHistory,typistShowHistory,
        showAiResult, setShowAiResult,
        showSavedReport, setShowSavedReport,
        cxrVersion,boneEnabled,
        advViewerUser, setAdvViewerUser,
        nonFrontalImages,
        forceReinferenceImageIds, 
        setForceReinferenceImageIds,
        showSharedViewer, setShowSharedViewer,
        isOutlineEnabled,
        setIsOutlineEnabled,
        isHeatMapEnabled,
        setIsHeatMapEnabled,
        showShortcutModal, setShowShortcutModal,
        weasisViewerUser, setWeasisViewerUser,
        infinitViewerUser, setInifinitViewerUser,
        infinitViewerUrl, setinfinitViewerUrl,
        accessionNumber, setAccessionNumber,
        keyword,setKeyword,
        weasisPatientDetails, setWeasisPatientDetails, 
        hideSeriesAndImageCount,
        isPriorsStudy,
        showFindingsHighlighted,
        showAboveThresholdScore,
        showProbablilityScores,
        prefetchCount,
        prefetchPaused,
        setPrefetchPaused,
        stackListSortedByImages,
        basicWeasisUser,
        setBasicWeasisUser,
        hpDataList, hpLoading, setHpLoading,
        s10User,
        s10Url,
        isLandscape,
        showPathologyOptions, setShowPathologyOptions,
        showTopBar, setShowTopBar,
        showSharedTopBar, setShowSharedTopBar,
        isHPApplied, setIsHpApplied,
        appliedHp, setAppliedHp,
        hpPercentageMap, setHpPercentageMap,
        hpCandidates, setHpCandidates,
        hpViewportMap, setHpViewportMap,
        hpNotFound, setHpNotFound,
        hpWillHang, setHpWillHang,
        twoMonitorEnabled, setTwoMonitorEnabled,
        //isEnabledSlabThickness, 
        maskImages, setMaskImages,
        checkForMaskingAnnotation, 
        cINEFramesPerSecond, setCINEFramesPerSecond,
        returnReasonList,
        showAccountWarning,
        setShowAccountWarning,
        accountWarningText,
        setAccountWarningText,
  }), [
    fetchStudyData, generateStudyData, addSingleSeriesToStudyData, waitForSeriesReadyAndMerge, waitForScSeriesAndMerge, getMessageCount, fetchPriorStudyData,
    generatePriorStudyData, fetchSharedStudyData, checkBypass, fetchMobileStudyData,
    hideSeriesAndImageCount, checkForMaskingAnnotation,
    studyResponseData, studyData, studyModality, studyParentId,
    showToast, assignmentData, studyBoundingBox, studyTextTool,
    studyPredictedPathology, viewportLoading,
    savedKeyImages, unsavedKeyImages, keyImages, keyObjectImages, prImages, qcData,
    studyHistoryData, studyClinicalNotesData, messageCount,
    progress, progressComplete, stackSize, dataGathered, thumbnailLoaded,
    priorStudyResponseData, priorStudyData, priorsByCondition, patientStudyData, impressions,
    downloadStudyEnabled, isAccessAllowed, notAllowedMessage,
    enableHeatmap, enableOutline, firmViewerId,
    reportDoneStatus, reportingDoctor, reportSavedBy, reportMenu, seriesMenu,
    isGenki, autoGenerateReportData, autoOpenReportPanel,
    meddreamFullUrl, advanceViewerURLIndex, advanceViewerSlotUUID, releaseAdvanceViewerSlot,
    priorStudyKeyImages, priorStudyKeyObjectImages, priorStudyPRImages, parentChildReportsObj,
    extReportRecieved, studyParentAssignmentData,
    aiProcessed, viewerOpacity, alreadyAddedBB,
    isBypassAllowed, isPasswordValid,
    sharedViewer, sharedDefaultViewer, sharedAdvanceViewerURL,
    showStartAlertToast, showAttachment, showClinicalNotes,
    showStudyExpirationPopup, showStudyAlreadyExpiredPopup, checkStudyExpired,
    isTypistWorkflowEnabled, showChats, typistShowHistory,
    showAiResult, showSavedReport, cxrVersion, boneEnabled,
    advViewerUser, nonFrontalImages, forceReinferenceImageIds, showSharedViewer,
    isOutlineEnabled, isHeatMapEnabled, showShortcutModal,
    weasisViewerUser, infinitViewerUser, infinitViewerUrl,
    accessionNumber, keyword, weasisPatientDetails,
    isPriorsStudy, showFindingsHighlighted, showAboveThresholdScore, showProbablilityScores,
    prefetchCount, stackListSortedByImages, basicWeasisUser,
    hpDataList, hpLoading, s10User, s10Url, isLandscape,
    showPathologyOptions, showTopBar, showSharedTopBar,
    isHPApplied, appliedHp, hpPercentageMap, hpCandidates, hpViewportMap, hpNotFound, hpWillHang,
    twoMonitorEnabled, maskImages,
    cINEFramesPerSecond, returnReasonList, showAccountWarning, accountWarningText,
  ]);

  return (
    <studyviewerContext.Provider value={contextValue}>
      {props.children}
    </studyviewerContext.Provider>
  );
};

export default StudyviewerState;

